import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { applyProductTheme } from './mapTheme';
import './App.css';

// Everything the basemap needs comes through this prefix, which nginx forwards
// to the front-end's proxy (app/map-tiles/[...path]/route.ts over there). The
// admin used to hold its own MapTiler key and send it from the browser: that
// key was readable by anyone who opened the public JS bundle, and the browser
// talked to api.maptiler.com directly, which is what fails on the networks
// behind ClickUp 86bbu046g. Neither is true any more — the admin ships no key
// at all and only ever talks to its own origin.
const MAP_PROXY_PREFIX = '/map-tiles/';

// Same light CARTO raster basemap used by the mobile app and the front-end
// demo — keeps the map looking consistent across all three clients. Only the
// fallback for when the styled basemap cannot be fetched.
const MAP_STYLE = {
  version: 8,
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        '/map-tiles/carto/a/light_all/{z}/{x}/{y}@2x.png',
        '/map-tiles/carto/b/light_all/{z}/{x}/{y}@2x.png',
        '/map-tiles/carto/c/light_all/{z}/{x}/{y}@2x.png',
        '/map-tiles/carto/d/light_all/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      maxzoom: 20,
      attribution: '© OpenStreetMap contributors © CARTO',
    },
  },
  layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
};
const TASHKENT_CENTER = { lng: 69.2401, lat: 41.2995 };

// MapLibre cannot take a root-relative URL in two places: `sprite` is parsed
// with `new URL(value)` and no base, and tile URLs become a `Request` inside a
// worker that has no document to resolve against. Both emptied the demo's map
// before the same fix landed there.
//
// Concatenated rather than run through `new URL()` — that parser percent-encodes
// `{` and `}`, which turns `{z}/{x}/{y}` and `{fontstack}/{range}` into literal
// text. These are our own paths, so there is nothing to resolve.
function toAbsoluteMapUrl(url) {
  if (typeof url !== 'string' || !url.startsWith(MAP_PROXY_PREFIX)) return url;
  return `${window.location.origin}${url}`;
}

function absolutizeStyleUrls(node) {
  if (typeof node === 'string') return toAbsoluteMapUrl(node);
  if (Array.isArray(node)) return node.map(absolutizeStyleUrls);

  if (node !== null && typeof node === 'object') {
    return Object.fromEntries(
      Object.entries(node).map(([key, value]) => [key, absolutizeStyleUrls(value)]),
    );
  }

  return node;
}

// Fetched rather than handed to MapLibre as a URL so `sprite` can be rewritten
// before it is parsed. MapLibre would have fetched the same document itself, so
// this costs no extra request.
async function getMapStyle() {
  try {
    const response = await fetch(`${MAP_PROXY_PREFIX}maptiler/maps/streets-v2/style.json`);

    if (response.ok) {
      const style = await response.json();
      applyProductTheme(style.layers);
      return absolutizeStyleUrls(style);
    }
  } catch {
    // Proxy or MapTiler unreachable — the raster fallback below still renders.
  }

  return absolutizeStyleUrls(MAP_STYLE);
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001').replace(
  /\/+$/,
  '',
);
const AUTH_STORAGE_KEY = 'brdgs_admin_auth_v1';

// Stroke paths rather than an icon dependency: a dozen icons do not justify a
// package, and the collapsed sidebar needs them to stay legible at 18px.
const NAV_ICON_PATHS = {
  map: ['M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z', 'M8 2v16', 'M16 6v16'],
  artifacts: ['M12 2L2 7l10 5 10-5-10-5z', 'M2 17l10 5 10-5', 'M2 12l10 5 10-5'],
  users: [
    'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2',
    'M9 3a4 4 0 100 8 4 4 0 000-8z',
    'M23 21v-2a4 4 0 00-3-3.87',
    'M16 3.13a4 4 0 010 7.75',
  ],
  // Clipboard with a check — the moderation queue.
  moderation: [
    'M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2',
    'M9 2h6v4H9z',
    'M9 14l2 2 4-4',
  ],
  creatorEntities: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z', 'M12 7a3 3 0 100 6 3 3 0 000-6z'],
  // Briefcase — the Деятели themselves, with their BB balance.
  creators: [
    'M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z',
    'M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16',
  ],
  potentialUsers: [
    'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2',
    'M8.5 3a4 4 0 100 8 4 4 0 000-8z',
    'M20 8v6',
    'M23 11h-6',
  ],
  tags: ['M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z', 'M7 7h.01'],
  chevronLeft: ['M15 18l-6-6 6-6'],
  chevronRight: ['M9 18l6-6-6-6'],
  logout: ['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
};

function NavIcon({ name }) {
  return (
    <svg
      aria-hidden="true"
      className="nav-icon"
      fill="none"
      height="18"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="18"
    >
      {(NAV_ICON_PATHS[name] || []).map((d) => (
        <path d={d} key={d} />
      ))}
    </svg>
  );
}

const tabs = [
  { id: 'map', label: 'Карта' },
  { id: 'moderation', label: 'Модерация' },
  { id: 'artifacts', label: 'Артефакты' },
  { id: 'users', label: 'Пользователи' },
  { id: 'creatorEntities', label: 'Точки Деятелей' },
  { id: 'creators', label: 'Деятели' },
  { id: 'potentialUsers', label: 'Потенциальные пользователи' },
  { id: 'tags', label: 'Теги' },
];

const REQUEST_STATUS_LABEL = {
  PENDING: 'Ждёт ответа',
  APPROVED: 'Одобрен',
  REJECTED: 'Отклонён',
};

// What the author reads when the admin leaves the message empty — the backend
// fills in this text (NotificationsService.notifyArtifactExtendedByAdmin).
const ARTIFACT_EXTEND_DEFAULT_MESSAGE = 'Подарок от команды Brdgs';

const POTENTIAL_USER_SOURCE_LABEL = {
  PERSONA: 'Личность',
  CREATOR: 'Деятель',
};

const CREATOR_ENTITY_TYPE_LABEL = {
  PLACE: 'Место',
  EVENT: 'Событие',
};

// Everything but APPROVED is off the public map. DEMO points belong to demo
// Деятели (onboarding without an invite, or an invite nobody claimed yet) and
// are seen by their author only — see the backend's CreatorEntityStatus.
const CREATOR_ENTITY_STATUS_LABEL = {
  DEMO: 'Демо (видна только автору)',
  PENDING: 'На модерации',
  REJECTED: 'Отклонена',
  ARCHIVED: 'В архиве (перенесена)',
};

const emptyDeyatelForm = {
  email: '',
  name: '',
  title: '',
  description: '',
  reachRadiusM: 300,
  lat: null,
  lng: null,
};

const emptyUserForm = {
  id: '',
  name: '',
  email: '',
  phone: '',
  role: 'USER',
  password: '',
};

function formatDate(value) {
  if (!value) return '—';

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function getIdentity(user) {
  return user?.email || user?.phone || user?.name || '—';
}

// Stub email of a web-demo guest's shadow User — same domain as the backend's
// GUEST_EMAIL_DOMAIN (users/public-user.ts).
function isGuestEmail(email) {
  return Boolean(email?.endsWith('@brdgs.local'));
}

// Artifact authors now come in the public shape — { id, name, avatarUrl,
// tier, isGuest }, no email or phone. The reports endpoints still send the old
// admin shape with an email, so both are read here.
function getAuthorIdentity(user) {
  if (!user) return '—';
  if (user.isGuest || isGuestEmail(user.email)) return 'Гость';

  const identity = user.name || user.email || user.phone || '—';
  return user.tier === 'INFLUENCER' ? `${identity} · Творец` : identity;
}

async function parseError(response) {
  try {
    const payload = await response.json();
    // Current backend: { statusCode, requestId, error: { code, message } }.
    // Older one answered a flat `message`, string or array — both still read,
    // so the panel does not depend on which backend it is talking to.
    const nested = payload?.error?.message;
    if (typeof nested === 'string' && nested.trim()) return nested;

    const message = payload?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string') return message;
  } catch {
    return `HTTP ${response.status}`;
  }

  return `HTTP ${response.status}`;
}

const AUTH_CHANGED_EVENT = 'brdgs-admin:auth-changed';

function decodeJwtExpiry(token) {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(normalized));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

// Dedupes concurrent refreshes (e.g. loadDashboard's Promise.all of 4
// requests all noticing the same stale token) into a single /auth/refresh
// call instead of firing one per caller.
let refreshPromise = null;

async function refreshAuth() {
  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  const stored = raw ? JSON.parse(raw) : null;

  if (!stored?.refreshToken) {
    throw new Error('Нет сохранённой сессии для обновления');
  }

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: stored.refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Не удалось обновить сессию');
  }

  const payload = await response.json();
  const nextAuth = payload.data;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  return nextAuth;
}

// Clears the stored session and notifies App() to fall back to the login
// screen — used when a refresh attempt itself fails (dead/expired refresh token).
function forceLogout() {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

async function ensureFreshToken() {
  try {
    refreshPromise = refreshPromise || refreshAuth().finally(() => {
      refreshPromise = null;
    });
    const nextAuth = await refreshPromise;
    return nextAuth.accessToken;
  } catch {
    forceLogout();
    throw new Error('Сессия истекла, войдите заново');
  }
}

async function request(path, options = {}, token, isRetry = false) {
  let effectiveToken = token;

  // Proactively refresh an expired/near-expired token before sending it,
  // rather than waiting for a 401 — some endpoints (e.g. GET /artifacts,
  // which guests can also call) use an *optional* auth guard that silently
  // treats an invalid/expired JWT as "no user" instead of rejecting it, so
  // the failure surfaces as a confusing business-logic 403 ("only for
  // admins") rather than 401. Reacting to 401 alone misses that case.
  if (token) {
    const expiresAt = decodeJwtExpiry(token);
    if (expiresAt && expiresAt < Date.now() + 5000) {
      effectiveToken = await ensureFreshToken();
    }
  }

  const headers = new Headers(options.headers);

  if (effectiveToken) {
    headers.set('Authorization', `Bearer ${effectiveToken}`);
  }

  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // Reactive fallback for guards that do reject a stale token with 401
  // outright (or clock skew slipping past the proactive check above).
  if (response.status === 401 && effectiveToken && !isRetry) {
    const nextToken = await ensureFreshToken();
    return request(path, options, nextToken, true);
  }

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  return response.json();
}

function LoginScreen({ onLogin }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    const normalizedLogin = login.trim();
    const body = normalizedLogin.includes('@')
      ? { email: normalizedLogin, password }
      : { username: normalizedLogin, password };

    try {
      const payload = await request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const auth = payload.data;

      if (auth?.user?.role !== 'ADMIN') {
        if (auth?.accessToken && auth?.sessionId) {
          await request(`/auth/sessions/${auth.sessionId}`, {
            method: 'DELETE',
          }, auth.accessToken).catch(() => null);
        }

        setError('Как вы сюда попали?');
        return;
      }

      onLogin(auth);
    } catch (err) {
      setError(err.message || 'Не удалось авторизоваться');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className="auth-page">
      <form className="auth-panel" onSubmit={handleSubmit}>
        <div>
          <p className="eyebrow">Brdgs admin</p>
          <h1>Вход</h1>
        </div>

        <label>
          Логин (обычный текст, не email)
          <input
            autoFocus
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            placeholder="admin-1"
            required
          />
        </label>

        <label>
          Пароль
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="••••••••"
            type="password"
            required
          />
        </label>

        {error && <p className="form-error">{error}</p>}

        <button className="primary-button" disabled={isLoading} type="submit">
          {isLoading ? 'Проверяем...' : 'Войти'}
        </button>
      </form>
    </main>
  );
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

// Renders a bold primary value with a muted secondary value right next to
// it on the same line (e.g. title + id) instead of the old stacked
// strong-over-small layout, which made every table row two lines tall.
// Both halves are width-capped and ellipsized so long titles/ids/reasons
// don't blow up the table's width — full text is still one hover away.
function Cell({ primary, secondary }) {
  return (
    <div className="cell">
      <strong title={typeof primary === 'string' ? primary : undefined}>{primary}</strong>
      {secondary && (
        <small title={typeof secondary === 'string' ? secondary : undefined}>{secondary}</small>
      )}
    </div>
  );
}

// Same width-capping/ellipsis treatment as Cell, for a single plain value
// (e.g. an identity string) rather than a primary/secondary pair.
function Truncate({ value, className }) {
  return (
    <span
      className={className ? `truncate ${className}` : 'truncate'}
      title={typeof value === 'string' ? value : undefined}
    >
      {value}
    </span>
  );
}

// A round thumbnail, or the name's first letter when there is no picture or it
// no longer loads — a replaced or refused avatar's file is deleted, so old
// moderation history points at nothing.
function UserAvatar({ url, name, size }) {
  const [hasFailed, setHasFailed] = useState(false);
  const src = toPublicMediaUrl(url);
  const className = size === 'large' ? 'user-avatar user-avatar--large' : 'user-avatar';

  useEffect(() => {
    setHasFailed(false);
  }, [src]);

  if (!src || hasFailed) {
    return (
      <span aria-hidden="true" className={className}>
        {(name || '?').trim().charAt(0).toUpperCase() || '?'}
      </span>
    );
  }

  return <img alt={name || ''} className={className} onError={() => setHasFailed(true)} src={src} />;
}

// Avatar next to a name/id pair, for the tables that list people.
function PersonCell({ avatarUrl, primary, secondary }) {
  return (
    <div className="person-cell">
      <UserAvatar name={typeof primary === 'string' ? primary : ''} url={avatarUrl} />
      <Cell primary={primary} secondary={secondary} />
    </div>
  );
}

const DEFAULT_PAGE_SIZE = 10;

function usePagination(items, pageSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    setPage(1);
  }, [items.length]);

  const pageItems = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize],
  );

  return { page: safePage, pageCount, pageItems, setPage };
}

function Pagination({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null;

  return (
    <div className="pagination">
      <button
        className="ghost-button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        type="button"
      >
        ← Назад
      </button>
      <span>
        Страница {page} из {pageCount}
      </span>
      <button
        className="ghost-button"
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        type="button"
      >
        Вперёд →
      </button>
    </div>
  );
}

function ArtifactTable({ artifacts, onExtend, onToggleHidden, onView }) {
  const { page, pageCount, pageItems, setPage } = usePagination(artifacts);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Автор</th>
              <th>Тип</th>
              <th>Реакции</th>
              <th>TTL</th>
              <th>Создан</th>
              <th>Видимость</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((artifact) => (
              <tr key={artifact.id}>
                <td>
                  <Cell primary={artifact.title} secondary={artifact.id} />
                </td>
                <td>
                  <Truncate value={getAuthorIdentity(artifact.user)} />
                </td>
                <td>{artifact.type || 'TEXT'}</td>
                <td>
                  +{artifact.likeCount || 0} / -{artifact.dislikeCount || 0} / ★
                  {artifact.favoriteCount || 0}
                </td>
                <td>{artifact.ttlDays ?? '—'} дн.</td>
                <td>{formatDate(artifact.createdAt)}</td>
                <td>
                  {artifact.isHidden && <span className="status-pill">Скрыт</span>}
                  <button className="ghost-button" onClick={() => onView(artifact)} type="button">
                    Посмотреть
                  </button>
                  <button
                    className="ghost-button"
                    onClick={() => onToggleHidden(artifact.id, !artifact.isHidden)}
                    type="button"
                  >
                    {artifact.isHidden ? 'Вернуть' : 'Скрыть'}
                  </button>
                  <button
                    className="ghost-button"
                    onClick={() => onExtend(artifact)}
                    title="Добавить дни жизни"
                    type="button"
                  >
                    + дни
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

function CreatorEntityRow({ entity, onDelete }) {
  const [notifyByEmail, setNotifyByEmail] = useState(true);

  function handleDelete() {
    if (!window.confirm(`Удалить точку «${entity.title}»? Это необратимо.`)) {
      return;
    }

    onDelete(entity.id, notifyByEmail);
  }

  return (
    <tr>
      <td>
        <Cell primary={entity.title} secondary={entity.id} />
      </td>
      <td>
        <Truncate value={getIdentity(entity.creator)} />
      </td>
      <td>{CREATOR_ENTITY_TYPE_LABEL[entity.type] || entity.type}</td>
      <td>{entity.reachRadiusM} м</td>
      <td>
        +{entity.likeCount || 0} / -{entity.dislikeCount || 0} / ★
        {entity.favoriteCount || 0}
      </td>
      <td>{formatDate(entity.createdAt)}</td>
      <td>
        {CREATOR_ENTITY_STATUS_LABEL[entity.status] ? (
          <span className="status-pill">{CREATOR_ENTITY_STATUS_LABEL[entity.status]}</span>
        ) : entity.isHidden ? (
          <span className="status-pill">Скрыта</span>
        ) : entity.expiresAt && new Date(entity.expiresAt) < new Date() ? (
          <span className="status-pill">Истекла</span>
        ) : (
          'Активна'
        )}
      </td>
      <td>
        <div className="row-actions">
          <label className="checkbox-field">
            <input
              checked={notifyByEmail}
              onChange={(event) => setNotifyByEmail(event.target.checked)}
              type="checkbox"
            />
            Email
          </label>
          <button className="danger-button" onClick={handleDelete} type="button">
            Удалить
          </button>
        </div>
      </td>
    </tr>
  );
}

function CreatorEntityTable({ entities, onDelete }) {
  const { page, pageCount, pageItems, setPage } = usePagination(entities);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Деятель</th>
              <th>Тип</th>
              <th>Радиус</th>
              <th>Реакции</th>
              <th>Создана</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((entity) => (
              <CreatorEntityRow entity={entity} key={entity.id} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

// ─── Модерация: one queue for everything that waits for an admin ──────────
// It used to be five sections (Жалобы, Артефакты с жалобами, Имена и
// аватары, Заявки на модерацию, Запросы BB). Now each waiting thing is an
// item with a type, oldest first, filtered by type; the actions are the same
// ones those sections had.

const MODERATION_TYPES = [
  { id: 'ARTIFACT_REPORT', label: 'Жалобы на артефакты' },
  { id: 'POINT_CREATE', label: 'Новые точки' },
  { id: 'POINT_MOVE', label: 'Переносы точек' },
  { id: 'PROFILE_NAME', label: 'Имена' },
  { id: 'PROFILE_AVATAR', label: 'Аватары' },
  { id: 'BB_REQUEST', label: 'Запросы BB' },
  { id: 'USER_REPORT', label: 'Жалобы на пользователей' },
  { id: 'ROUTE', label: 'Маршруты' },
];

const MODERATION_TYPE_LABEL = Object.fromEntries(
  MODERATION_TYPES.map((type) => [type.id, type.label]),
);

const MODERATION_MODES = [
  { id: 'PENDING', label: 'Ждут решения' },
  { id: 'DECIDED', label: 'Решённые' },
];

const REPORT_STATUS_LABEL = {
  OPEN: 'Открыта',
  RESOLVED: 'Решена',
  REJECTED: 'Отклонена',
};

function byDate(item) {
  return new Date(item.submittedAt).getTime() || 0;
}

// An artifact with open reports is one item (its reports listed inside), a
// point request one per request, a name or avatar one per change.
function buildModerationQueue({
  reportedArtifacts,
  moderationRequests,
  profileRequests,
  bbRequests,
  userReports,
  routeRequests,
}) {
  const items = [];

  routeRequests.forEach((route) => {
    items.push({
      key: `route:${route.id}`,
      type: 'ROUTE',
      submittedAt: route.submittedAt || route.createdAt,
      data: route,
    });
  });

  userReports
    .filter((userReport) => userReport.status === 'OPEN')
    .forEach((userReport) => {
      items.push({
        key: `user-report:${userReport.id}`,
        type: 'USER_REPORT',
        submittedAt: userReport.createdAt,
        data: userReport,
      });
    });

  reportedArtifacts.forEach((artifact) => {
    const open = (artifact.reports || []).filter((report) => report.status === 'OPEN');
    if (open.length === 0) return;
    items.push({
      key: `report:${artifact.id}`,
      type: 'ARTIFACT_REPORT',
      submittedAt: open.map((report) => report.createdAt).sort()[0],
      data: artifact,
    });
  });

  moderationRequests.forEach((entity) => {
    items.push({
      key: `point:${entity.id}`,
      type: entity.movedFrom ? 'POINT_MOVE' : 'POINT_CREATE',
      submittedAt: entity.submittedAt || entity.createdAt,
      data: entity,
    });
  });

  profileRequests
    .filter((profileRequest) => profileRequest.status === 'PENDING')
    .forEach((profileRequest) => {
      items.push({
        key: `profile:${profileRequest.id}`,
        type: profileRequest.kind === 'AVATAR' ? 'PROFILE_AVATAR' : 'PROFILE_NAME',
        submittedAt: profileRequest.createdAt,
        data: profileRequest,
      });
    });

  bbRequests
    .filter((bbRequest) => bbRequest.status === 'PENDING')
    .forEach((bbRequest) => {
      items.push({
        key: `bb:${bbRequest.id}`,
        type: 'BB_REQUEST',
        submittedAt: bbRequest.createdAt,
        data: bbRequest,
      });
    });

  return items.sort((a, b) => byDate(a) - byDate(b));
}

// Decided things, newest first. Points have no list of past decisions (each
// point keeps its own history), so they don't show up here.
function buildModerationHistory({
  reports,
  profileRequestHistory,
  bbRequests,
  userReports,
  routeHistory,
}) {
  const items = [];

  routeHistory.forEach((route) => {
    items.push({
      key: `route:${route.id}`,
      type: 'ROUTE',
      submittedAt: route.decidedAt || route.submittedAt,
      data: route,
    });
  });

  userReports
    .filter((userReport) => userReport.status !== 'OPEN')
    .forEach((userReport) => {
      items.push({
        key: `user-report:${userReport.id}`,
        type: 'USER_REPORT',
        submittedAt: userReport.decidedAt || userReport.createdAt,
        data: userReport,
      });
    });

  reports
    .filter((report) => report.status !== 'OPEN')
    .forEach((report) => {
      items.push({
        key: `report:${report.id}`,
        type: 'ARTIFACT_REPORT',
        submittedAt: report.createdAt,
        data: report,
      });
    });

  profileRequestHistory.forEach((profileRequest) => {
    items.push({
      key: `profile:${profileRequest.id}`,
      type: profileRequest.kind === 'AVATAR' ? 'PROFILE_AVATAR' : 'PROFILE_NAME',
      submittedAt: profileRequest.decidedAt || profileRequest.createdAt,
      data: profileRequest,
    });
  });

  bbRequests
    .filter((bbRequest) => bbRequest.status !== 'PENDING')
    .forEach((bbRequest) => {
      items.push({
        key: `bb:${bbRequest.id}`,
        type: 'BB_REQUEST',
        submittedAt: bbRequest.decidedAt || bbRequest.createdAt,
        data: bbRequest,
      });
    });

  return items.sort((a, b) => byDate(b) - byDate(a));
}

function useBusy() {
  const [isBusy, setIsBusy] = useState(false);

  async function run(action) {
    setIsBusy(true);
    try {
      await action();
    } finally {
      setIsBusy(false);
    }
  }

  return [isBusy, run];
}

const QUEUE_COLUMNS = 5;

function TypeCell({ type }) {
  return (
    <td>
      <span className="status-pill">{MODERATION_TYPE_LABEL[type] || type}</span>
    </td>
  );
}

// The refusal reason, in a row of its own under the item. `minLength` 0
// makes it optional.
function RejectReasonRow({ isBusy, minLength, onSubmit, placeholder }) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isValid = trimmed.length >= minLength;

  return (
    <tr>
      <td colSpan={QUEUE_COLUMNS}>
        <form
          className="row-actions"
          onSubmit={(event) => {
            event.preventDefault();
            if (isValid) onSubmit(trimmed);
          }}
        >
          <textarea
            autoFocus
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            placeholder={placeholder}
            required={minLength > 0}
            rows={2}
            style={{ flex: 1, minWidth: 280 }}
            value={reason}
          />
          <button className="danger-button" disabled={isBusy || !isValid} type="submit">
            {trimmed || minLength > 0 ? 'Отклонить с причиной' : 'Отклонить без причины'}
          </button>
        </form>
      </td>
    </tr>
  );
}

function ApproveRejectButtons({ isBusy, onApprove, onToggleReject }) {
  return (
    <div className="row-actions">
      <button className="primary-button" disabled={isBusy} onClick={onApprove} type="button">
        Одобрить
      </button>
      <button className="danger-button" disabled={isBusy} onClick={onToggleReject} type="button">
        Отклонить
      </button>
    </div>
  );
}

// A Деятель's new point or transfer: approved, or refused with a reason the
// Деятель sees in «Мои точки», a push and a mail. Refusing a new point gives
// its BB back.
function PointRequestRow({ item, onApprove, onReject }) {
  const entity = item.data;
  const [isRejecting, setIsRejecting] = useState(false);
  const [isBusy, run] = useBusy();
  const mapUrl = `https://www.openstreetmap.org/?mlat=${entity.lat}&mlon=${entity.lng}#map=17/${entity.lat}/${entity.lng}`;
  const previousRejections = (entity.moderationDecisions || []).filter(
    (decision) => decision.outcome === 'REJECTED',
  );

  return (
    <>
      <tr>
        <TypeCell type={item.type} />
        <td className="wrap-cell">
          <Cell primary={entity.title} secondary={entity.description || 'Без описания'} />
          <div className="muted">
            {CREATOR_ENTITY_TYPE_LABEL[entity.type] || entity.type}
            {entity.startAt && entity.endAt
              ? ` · ${formatDate(entity.startAt)} — ${formatDate(entity.endAt)}`
              : ''}
            {' · '}
            <a href={mapUrl} rel="noreferrer" target="_blank">
              на карте
            </a>
            {` · ${entity.reachRadiusM} м`}
          </div>
          {entity.movedFrom && (
            <div className="muted">
              Перенос точки «{entity.movedFrom.title}» —{' '}
              <a
                href={`https://www.openstreetmap.org/?mlat=${entity.movedFrom.lat}&mlon=${entity.movedFrom.lng}#map=17/${entity.movedFrom.lat}/${entity.movedFrom.lng}`}
                rel="noreferrer"
                target="_blank"
              >
                старое место
              </a>
            </div>
          )}
          {previousRejections.length > 0 && (
            <div className="muted">
              Уже отклонялась: {previousRejections[0].reason || 'без причины'}
            </div>
          )}
        </td>
        <td>
          <Truncate value={getIdentity(entity.creator)} />
        </td>
        <td>{formatDate(item.submittedAt)}</td>
        <td>
          <ApproveRejectButtons
            isBusy={isBusy}
            onApprove={() => run(() => onApprove(entity.id))}
            onToggleReject={() => setIsRejecting((value) => !value)}
          />
        </td>
      </tr>
      {isRejecting && (
        <RejectReasonRow
          isBusy={isBusy}
          minLength={3}
          onSubmit={(reason) => run(() => onReject(entity.id, reason))}
          placeholder="Причина отказа — её увидит Деятель"
        />
      )}
    </>
  );
}

// ─── Маршруты ──────────────────────────────────────────────────────────────
// A Личность's recorded walk (docs/knowledge-base/specs/routes-places-gallery.md):
// the track, places along it with notes and media, media of the whole route.
// Every route waits here before anyone else sees it.

function formatDistance(meters) {
  if (!Number.isFinite(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} м`;
  return `${(meters / 1000).toFixed(1).replace('.', ',')} км`;
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '—';
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} мин`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} ч ${rest} мин` : `${Math.floor(minutes / 60)} ч`;
}

// The route's media rows carry a type (IMAGE / VIDEO / AUDIO) as well as the
// mime type; either is enough to pick the player.
function MediaItem({ media }) {
  const kind = resolveMediaKind(media.mimeType) || String(media.type || '').toLowerCase();
  const src = toPublicMediaUrl(media.url) || media.url;
  const name = media.originalName || '';

  if (kind === 'image') {
    return (
      <a className="route-media__image" href={src} rel="noreferrer" target="_blank" title={name}>
        <img alt={name} loading="lazy" src={src} />
      </a>
    );
  }
  if (kind === 'video') {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video className="route-media__video" controls preload="metadata" src={src} title={name} />;
  }
  if (kind === 'audio') {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <audio className="route-media__audio" controls preload="metadata" src={src} title={name} />;
  }
  return (
    <a href={src} rel="noreferrer" target="_blank">
      {name || 'Файл'}
    </a>
  );
}

function MediaList({ media }) {
  if (!media?.length) return null;

  return (
    <div className="route-media">
      {media.map((item) => (
        <MediaItem key={item.id} media={item} />
      ))}
    </div>
  );
}

function buildPin(className, label, title) {
  const element = document.createElement('div');
  element.className = `route-pin ${className}`;
  element.textContent = label;
  element.title = title;
  return element;
}

function getRouteBounds(route) {
  const b = route.bounds;
  if (b && [b.minLat, b.maxLat, b.minLng, b.maxLng].every(Number.isFinite)) {
    return [
      [b.minLng, b.minLat],
      [b.maxLng, b.maxLat],
    ];
  }

  const lats = route.path.map(([lat]) => lat);
  const lngs = route.path.map(([, lng]) => lng);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

// The track on the product's map: a black line with a white casing, the
// start and the finish, and the numbered places. Scroll zoom is off so the
// queue scrolls past it; the +/- buttons and dragging still work.
function RouteMapPreview({ route }) {
  const containerRef = useRef(null);
  const path = route.path || [];
  const hasPath = path.length >= 2;

  useEffect(() => {
    if (!hasPath) return undefined;

    let map = null;
    let cancelled = false;

    (async () => {
      const style = await getMapStyle();
      if (cancelled || !containerRef.current) return;

      map = new maplibregl.Map({
        container: containerRef.current,
        style,
        bounds: getRouteBounds(route),
        fitBoundsOptions: { padding: 36, maxZoom: 17 },
        scrollZoom: false,
        attributionControl: { compact: true },
        transformRequest: (url) =>
          url.startsWith(MAP_PROXY_PREFIX) ? { url: toAbsoluteMapUrl(url) } : undefined,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      map.on('load', () => {
        map.addSource('route-path', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: path.map(([lat, lng]) => [lng, lat]) },
          },
        });
        const layout = { 'line-join': 'round', 'line-cap': 'round' };
        map.addLayer({
          id: 'route-path-casing',
          type: 'line',
          source: 'route-path',
          layout,
          paint: { 'line-color': '#FFFFFF', 'line-width': 8 },
        });
        map.addLayer({
          id: 'route-path-line',
          type: 'line',
          source: 'route-path',
          layout,
          paint: { 'line-color': '#111111', 'line-width': 4 },
        });
      });

      const [startLat, startLng] = path[0];
      const [endLat, endLng] = path[path.length - 1];
      new maplibregl.Marker({ element: buildPin('route-pin--start', 'С', 'Старт') })
        .setLngLat([startLng, startLat])
        .addTo(map);
      new maplibregl.Marker({ element: buildPin('route-pin--finish', 'Ф', 'Финиш') })
        .setLngLat([endLng, endLat])
        .addTo(map);
      (route.spots || []).forEach((spot, index) => {
        new maplibregl.Marker({
          element: buildPin('route-pin--spot', String(index + 1), spot.text || `Место ${index + 1}`),
        })
          .setLngLat([spot.lng, spot.lat])
          .addTo(map);
      });
    })();

    return () => {
      cancelled = true;
      map?.remove();
    };
    // The queue never edits a route in place: a new id is a new row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.id, hasPath]);

  if (!hasPath) return <div className="route-map route-map--empty">Трек не пришёл</div>;
  return <div className="route-map" ref={containerRef} />;
}

const ROUTE_STATUS_LABEL = {
  APPROVED: 'Одобрен',
  REJECTED: 'Отклонён',
};

// The row says who and what; the line under it shows the walk itself — the
// map, every place's note and media, the route's own media — since that is
// what the decision is about. In «Решённые» the walk stays folded behind
// «Маршрут»: there it is a record of a decision, and a page of maps would be
// a lot to open at once.
function RouteRequestRow({ isHistory, item, onApprove, onReject }) {
  const route = item.data;
  const user = route.user || {};
  const spots = route.spots || [];
  const mediaCount =
    (route.media || []).length + spots.reduce((sum, spot) => sum + (spot.media || []).length, 0);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!isHistory);
  const [isBusy, run] = useBusy();

  return (
    <>
      <tr>
        <TypeCell type={item.type} />
        <td className="wrap-cell">
          <strong>{route.title}</strong>
          <div className="muted">
            {formatDistance(route.distanceM)} · {formatDuration(route.durationS)} · мест:{' '}
            {spots.length} · медиа: {mediaCount}
          </div>
        </td>
        <td>
          <PersonCell
            avatarUrl={user.avatarUrl}
            primary={getAuthorIdentity(user)}
            secondary={user.id}
          />
        </td>
        <td>{formatDate(item.submittedAt)}</td>
        <td className={isHistory ? 'wrap-cell' : undefined}>
          {isHistory ? (
            <>
              <div className="row-actions">
                <span className="status-pill">{ROUTE_STATUS_LABEL[route.status] || route.status}</span>
                <button
                  className="ghost-button"
                  onClick={() => setIsExpanded((value) => !value)}
                  type="button"
                >
                  {isExpanded ? 'Свернуть' : 'Маршрут'}
                </button>
              </div>
              {route.rejectionReason && <div className="muted">Причина: {route.rejectionReason}</div>}
              {route.adminUser && <div className="muted">Решил: {getIdentity(route.adminUser)}</div>}
            </>
          ) : (
            <ApproveRejectButtons
              isBusy={isBusy}
              onApprove={() => run(() => onApprove(route))}
              onToggleReject={() => setIsRejecting((value) => !value)}
            />
          )}
        </td>
      </tr>
      {!isHistory && isRejecting && (
        <RejectReasonRow
          isBusy={isBusy}
          minLength={3}
          onSubmit={(reason) => run(() => onReject(route, reason))}
          placeholder="Причина отказа — её увидит автор маршрута"
        />
      )}
      {isExpanded && <RouteDetailsRow route={route} />}
    </>
  );
}

function RouteDetailsRow({ route }) {
  const spots = route.spots || [];

  return (
    <tr className="route-details-row">
      <td colSpan={QUEUE_COLUMNS}>
        <div className="route-details">
          <RouteMapPreview route={route} />
          <div className="route-details__body">
            <section>
              <h4>Описание</h4>
              {route.description ? (
                <p className="map-preview__text">{route.description}</p>
              ) : (
                <p className="muted">Без описания</p>
              )}
            </section>
            {route.media?.length > 0 && (
              <section>
                <h4>Медиа маршрута</h4>
                <MediaList media={route.media} />
              </section>
            )}
            <section>
              <h4>Места на маршруте</h4>
              {spots.length ? (
                <ol className="route-spots">
                  {spots.map((spot, index) => (
                    <li key={spot.id}>
                      <span className="route-pin route-pin--spot">{index + 1}</span>
                      <div className="route-spots__body">
                        <p className="map-preview__text">{spot.text || 'Без заметки'}</p>
                        <MediaList media={spot.media} />
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted">Автор не отметил мест</p>
              )}
            </section>
          </div>
        </div>
      </td>
    </tr>
  );
}

// A custom name or a new avatar. A refusal reason reaches the user in a
// PROFILE_MODERATED notification.
function ProfileRequestQueueRow({ item, onApprove, onReject }) {
  const profileRequest = item.data;
  const user = profileRequest.user || {};
  const isPending = profileRequest.status === 'PENDING';
  const [isRejecting, setIsRejecting] = useState(false);
  const [isBusy, run] = useBusy();

  return (
    <>
      <tr>
        <TypeCell type={item.type} />
        <td>
          {profileRequest.kind === 'AVATAR' ? (
            <UserAvatar name={user.name} size="large" url={profileRequest.avatarUrl} />
          ) : (
            <strong>{profileRequest.name}</strong>
          )}
        </td>
        <td>
          <PersonCell avatarUrl={user.avatarUrl} primary={user.name || '—'} secondary={user.email || user.id} />
        </td>
        <td>{formatDate(item.submittedAt)}</td>
        <td>
          {isPending ? (
            <ApproveRejectButtons
              isBusy={isBusy}
              onApprove={() => run(() => onApprove(profileRequest))}
              onToggleReject={() => setIsRejecting((value) => !value)}
            />
          ) : (
            <div className="wrap-cell">
              <span className="status-pill">
                {REQUEST_STATUS_LABEL[profileRequest.status] || profileRequest.status}
              </span>
              {profileRequest.rejectionReason && (
                <div className="muted">Причина: {profileRequest.rejectionReason}</div>
              )}
            </div>
          )}
        </td>
      </tr>
      {isPending && isRejecting && (
        <RejectReasonRow
          isBusy={isBusy}
          minLength={0}
          onSubmit={(reason) => run(() => onReject(profileRequest, reason))}
          placeholder="Причина отказа — её увидит пользователь (необязательно)"
        />
      )}
    </>
  );
}

function BbRequestQueueRow({ item, onResolve }) {
  const bbRequest = item.data;

  return (
    <tr>
      <TypeCell type={item.type} />
      <td className="wrap-cell">
        {bbRequest.message}
        <div className="muted">
          {bbRequest.requestedAmount != null
            ? `Просит ${bbRequest.requestedAmount} BB`
            : 'Сумму не указал'}
        </div>
      </td>
      <td>
        <Cell
          primary={bbRequest.creator?.name || 'Без имени'}
          secondary={bbRequest.creator?.email || '—'}
        />
        <div className="muted">Баланс: {bbRequest.creator?.bbBalance ?? 0} BB</div>
      </td>
      <td>{formatDate(item.submittedAt)}</td>
      <td className="wrap-cell">
        {bbRequest.status === 'PENDING' ? (
          <button className="primary-button" onClick={() => onResolve(bbRequest)} type="button">
            Ответить
          </button>
        ) : (
          <>
            <span className="status-pill">
              {REQUEST_STATUS_LABEL[bbRequest.status] || bbRequest.status}
            </span>
            <div className="muted">Начислено {bbRequest.grantedAmount ?? 0} BB</div>
            {bbRequest.adminReply && <div className="muted">«{bbRequest.adminReply}»</div>}
          </>
        )}
      </td>
    </tr>
  );
}

// An artifact with its open reports: each report closed on its own, the
// artifact hidden, given days, or opened.
function ArtifactReportQueueRow({ item, onExtend, onToggleHidden, onUpdateStatus, onView }) {
  const artifact = item.data;
  const openReports = (artifact.reports || []).filter((report) => report.status === 'OPEN');

  return (
    <tr>
      <TypeCell type={item.type} />
      <td>
        <Cell primary={artifact.title} secondary={`Открытых жалоб: ${openReports.length}`} />
        <div className="report-list">
          {openReports.map((report) => {
            const reportMeta = `${getIdentity(report.reporter)} · ${formatDate(report.createdAt)}`;

            return (
              <div className="report-line" key={report.id}>
                <span className="report-line-text">
                  <strong title={report.reason || 'Без причины'}>
                    {report.reason || 'Без причины'}
                  </strong>
                  <small title={reportMeta}>{reportMeta}</small>
                </span>
                <div className="row-actions">
                  <button
                    className="ghost-button"
                    onClick={() => onUpdateStatus(report.id, 'RESOLVED')}
                    type="button"
                  >
                    Решено
                  </button>
                  <button
                    className="ghost-button"
                    onClick={() => onUpdateStatus(report.id, 'REJECTED')}
                    type="button"
                  >
                    Отклонить
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </td>
      <td>
        <Truncate value={getAuthorIdentity(artifact.user)} />
      </td>
      <td>{formatDate(item.submittedAt)}</td>
      <td>
        <div className="row-actions">
          {artifact.isHidden && <span className="status-pill">Скрыт</span>}
          <button className="ghost-button" onClick={() => onView(artifact)} type="button">
            Посмотреть
          </button>
          <button
            className="ghost-button"
            onClick={() => onToggleHidden(artifact.id, !artifact.isHidden)}
            type="button"
          >
            {artifact.isHidden ? 'Вернуть' : 'Скрыть'}
          </button>
          <button
            className="ghost-button"
            onClick={() => onExtend(artifact)}
            title="Добавить дни жизни"
            type="button"
          >
            + дни
          </button>
        </div>
      </td>
    </tr>
  );
}

// A complaint about a person, made from a signal (Сигналы вокруг). Closing
// it is all there is for now: there is no ban yet.
function UserReportQueueRow({ item, onDecide }) {
  const userReport = item.data;
  const [isBusy, run] = useBusy();
  const target = userReport.target || {};

  return (
    <tr>
      <TypeCell type={item.type} />
      <td className="wrap-cell">
        <strong>{userReport.reason || 'Без причины'}</strong>
        <div className="muted">
          {userReport.signal ? `По сигналу от ${formatDate(userReport.signal.createdAt)}` : 'Без сигнала'}
          {' · пожаловался '}
          {getIdentity(userReport.reporter)}
        </div>
      </td>
      <td>
        <PersonCell avatarUrl={target.avatarUrl} primary={target.name || '—'} secondary={target.email || target.id} />
      </td>
      <td>{formatDate(item.submittedAt)}</td>
      <td>
        {userReport.status === 'OPEN' ? (
          <div className="row-actions">
            <button
              className="ghost-button"
              disabled={isBusy}
              onClick={() => run(() => onDecide(userReport.id, 'RESOLVED'))}
              type="button"
            >
              Решено
            </button>
            <button
              className="ghost-button"
              disabled={isBusy}
              onClick={() => run(() => onDecide(userReport.id, 'REJECTED'))}
              type="button"
            >
              Отклонить
            </button>
          </div>
        ) : (
          <span className="status-pill">{REPORT_STATUS_LABEL[userReport.status] || userReport.status}</span>
        )}
      </td>
    </tr>
  );
}

// A decided report, in the history.
function ReportHistoryRow({ item }) {
  const report = item.data;

  return (
    <tr>
      <TypeCell type={item.type} />
      <td>
        <Cell primary={report.reason || 'Без причины'} secondary={report.artifact?.title || report.artifactId} />
      </td>
      <td>
        <Truncate value={getIdentity(report.reporter)} />
      </td>
      <td>{formatDate(item.submittedAt)}</td>
      <td>
        <span className="status-pill">{REPORT_STATUS_LABEL[report.status] || report.status}</span>
      </td>
    </tr>
  );
}

function ModerationRow({ item, handlers, isHistory }) {
  switch (item.type) {
    case 'ARTIFACT_REPORT':
      return isHistory ? (
        <ReportHistoryRow item={item} />
      ) : (
        <ArtifactReportQueueRow
          item={item}
          onExtend={handlers.onExtendArtifact}
          onToggleHidden={handlers.onToggleArtifactHidden}
          onUpdateStatus={handlers.onUpdateReportStatus}
          onView={handlers.onViewArtifact}
        />
      );
    case 'POINT_CREATE':
    case 'POINT_MOVE':
      return (
        <PointRequestRow
          item={item}
          onApprove={handlers.onApprovePoint}
          onReject={handlers.onRejectPoint}
        />
      );
    case 'PROFILE_NAME':
    case 'PROFILE_AVATAR':
      return (
        <ProfileRequestQueueRow
          item={item}
          onApprove={handlers.onApproveProfile}
          onReject={handlers.onRejectProfile}
        />
      );
    case 'BB_REQUEST':
      return <BbRequestQueueRow item={item} onResolve={handlers.onResolveBbRequest} />;
    case 'USER_REPORT':
      return <UserReportQueueRow item={item} onDecide={handlers.onDecideUserReport} />;
    case 'ROUTE':
      return (
        <RouteRequestRow
          isHistory={isHistory}
          item={item}
          onApprove={handlers.onApproveRoute}
          onReject={handlers.onRejectRoute}
        />
      );
    default:
      return null;
  }
}

function ModerationPanel({ handlers, history, mode, onModeChange, onTypeChange, queue, type }) {
  const isHistory = mode === 'DECIDED';
  const items = isHistory ? history : queue;
  const countByType = items.reduce((counts, item) => {
    counts[item.type] = (counts[item.type] || 0) + 1;
    return counts;
  }, {});
  const typeFilters = [
    { id: '', label: `Все · ${items.length}` },
    ...MODERATION_TYPES.filter((option) => countByType[option.id]).map((option) => ({
      id: option.id,
      label: `${option.label} · ${countByType[option.id]}`,
    })),
  ];
  // Deciding the last item of the chosen type takes its filter away with it:
  // the list falls back to «Все» rather than to an empty «Всё разобрано» with
  // nothing selected.
  const activeType = type && countByType[type] ? type : '';
  const shown = activeType ? items.filter((item) => item.type === activeType) : items;
  const { page, pageCount, pageItems, setPage } = usePagination(shown);

  useEffect(() => {
    if (type && !activeType) onTypeChange('');
  }, [type, activeType, onTypeChange]);

  return (
    <div className="panel-with-toolbar">
      <FilterSwitch filters={MODERATION_MODES} onChange={onModeChange} value={mode} />
      <FilterSwitch filters={typeFilters} onChange={onTypeChange} value={activeType} />
      {shown.length ? (
        <div className="table-panel">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Тип</th>
                  <th>Что</th>
                  <th>Кто</th>
                  <th>{isHistory ? 'Когда' : 'Подано'}</th>
                  <th>Решение</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item) => (
                  <ModerationRow handlers={handlers} isHistory={isHistory} item={item} key={item.key} />
                ))}
              </tbody>
            </table>
          </div>
          <Pagination onChange={setPage} page={page} pageCount={pageCount} />
        </div>
      ) : (
        <EmptyState>
          {isHistory ? 'Решённых обращений пока нет' : 'Всё разобрано — новых обращений нет'}
        </EmptyState>
      )}
      {isHistory && (
        <p className="muted">
          Решения по точкам Деятелей здесь не собираются — история каждой точки видна в ней самой.
        </p>
      )}
    </div>
  );
}

function TagsTable({ tags, onDelete }) {
  const { page, pageCount, pageItems, setPage } = usePagination(tags);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Тег</th>
              <th>Использований</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item) => (
              <tr key={item.tag}>
                <td>#{item.tag}</td>
                <td>{item.count}</td>
                <td className="row-actions row-actions--end">
                  <button
                    className="danger-button"
                    onClick={() => onDelete(item.tag)}
                    type="button"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

function PotentialUsersTable({ potentialUsers }) {
  const { page, pageCount, pageItems, setPage } = usePagination(potentialUsers);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Оставлено как</th>
              <th>Аккаунт</th>
              <th>Оставлен</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <strong>{lead.email}</strong>
                </td>
                <td>
                  <span className="status-pill">
                    {POTENTIAL_USER_SOURCE_LABEL[lead.source] || lead.source}
                  </span>
                </td>
                <td>
                  <Cell
                    primary={getIdentity(lead.user || lead.creator)}
                    secondary={lead.userId || lead.creatorId}
                  />
                </td>
                <td>{formatDate(lead.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

function UserForm({ form, setForm, onSubmit, onCancel, isEditing }) {
  return (
    <form className="user-form" onSubmit={onSubmit}>
      <input
        value={form.name}
        onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
        placeholder="Имя"
        required
      />
      <input
        value={form.email}
        onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
        placeholder="Email"
        type="email"
      />
      <input
        value={form.phone}
        onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
        placeholder="Телефон"
      />
      <select
        value={form.role}
        onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
      >
        <option value="USER">USER</option>
        <option value="ADMIN">ADMIN</option>
      </select>
      <input
        value={form.password}
        onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
        placeholder={isEditing ? 'Новый пароль' : 'Пароль'}
        type="password"
        required={!isEditing}
      />
      <div className="form-actions">
        <button className="primary-button" type="submit">
          {isEditing ? 'Обновить' : 'Добавить'}
        </button>
        {isEditing && (
          <button className="ghost-button" onClick={onCancel} type="button">
            Отмена
          </button>
        )}
      </div>
    </form>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="ghost-button" onClick={onClose} type="button">
            Закрыть
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function UsersTable({ users, onEdit, onDelete, onSetTier }) {
  const { page, pageCount, pageItems, setPage } = usePagination(users);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Контакты</th>
              <th>Роль</th>
              <th>Творец</th>
              <th>Создан</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((user) => {
              const isInfluencer = user.tier === 'INFLUENCER';
              // A guest has no public name or avatar, so there is nobody to
              // make a Творец of until they save the account.
              const isGuest = isGuestEmail(user.email);

              return (
                <tr key={user.id}>
                  <td>
                    <PersonCell avatarUrl={user.avatarUrl} primary={user.name} secondary={user.id} />
                  </td>
                  <td>
                    <Cell primary={user.email || '—'} secondary={user.phone || '—'} />
                  </td>
                  <td>
                    <span className="status-pill">{user.role}</span>
                    {isGuest && <span className="status-pill">Гость</span>}
                  </td>
                  <td>{isInfluencer ? <span className="status-pill">Творец</span> : '—'}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td className="row-actions">
                    {!isGuest && (
                      <button
                        className="ghost-button"
                        onClick={() => onSetTier(user, isInfluencer ? 'STANDARD' : 'INFLUENCER')}
                        type="button"
                      >
                        {isInfluencer ? 'Снять статус Творца' : 'Сделать Творцом'}
                      </button>
                    )}
                    <button className="ghost-button" onClick={() => onEdit(user)} type="button">
                      Изменить
                    </button>
                    <button
                      className="danger-button"
                      onClick={() => onDelete(user.id)}
                      type="button"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

function UsersPanel({
  users,
  form,
  setForm,
  onSubmit,
  onEdit,
  onDelete,
  onCancel,
  onOpenCreate,
  onSetTier,
  isFormOpen,
}) {
  const isEditing = Boolean(form.id);

  return (
    <div className="panel-with-toolbar">
      <div className="panel-toolbar">
        <button className="primary-button" onClick={onOpenCreate} type="button">
          Добавить пользователя
        </button>
      </div>

      <UsersTable onDelete={onDelete} onEdit={onEdit} onSetTier={onSetTier} users={users} />

      {isFormOpen && (
        <Modal
          onClose={onCancel}
          title={isEditing ? 'Редактировать пользователя' : 'Новый пользователь'}
        >
          <UserForm
            form={form}
            isEditing={isEditing}
            onCancel={onCancel}
            onSubmit={onSubmit}
            setForm={setForm}
          />
        </Modal>
      )}
    </div>
  );
}

// Segmented switch over a list's status filter, in the toolbar above a table.
function FilterSwitch({ filters, value, onChange }) {
  return (
    <div className="panel-toolbar panel-toolbar--start">
      {filters.map((filter) => (
        <button
          className={value === filter.id ? 'primary-button' : 'ghost-button'}
          key={filter.id || 'all'}
          onClick={() => onChange(filter.id)}
          type="button"
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
}

// Parses a whole number typed into an input; NaN for anything else, so a
// half-typed "1.5" or "" never reaches the API.
function parseWholeNumber(value) {
  const trimmed = String(value).trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : NaN;
}

function CreatorsTable({ creators, onGrant }) {
  const { page, pageCount, pageItems, setPage } = usePagination(creators);

  return (
    <div className="table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Деятель</th>
              <th>Почта</th>
              <th>Баланс BB</th>
              <th>Точек</th>
              <th>Демо</th>
              <th>Запрос BB</th>
              <th>Создан</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((creator) => (
              <tr key={creator.id}>
                <td>
                  <Cell primary={creator.name || 'Без имени'} secondary={creator.id} />
                </td>
                <td>
                  <Truncate className="truncate--wide" value={creator.email || '—'} />
                </td>
                <td>
                  <strong>{creator.bbBalance ?? 0}</strong>
                </td>
                <td>{creator.entityCount ?? 0}</td>
                <td>{creator.isDemo ? <span className="status-pill">Демо</span> : '—'}</td>
                <td>
                  {creator.hasPendingBbRequest ? (
                    <span className="status-pill">Ждёт ответа</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{formatDate(creator.createdAt)}</td>
                <td className="row-actions row-actions--end">
                  <button className="primary-button" onClick={() => onGrant(creator)} type="button">
                    Начислить BB
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination onChange={setPage} page={page} pageCount={pageCount} />
    </div>
  );
}

// The modals below hand the request to AdminApp and show its failure inline:
// the panel-wide error banner sits under the overlay, where it reads as if
// nothing happened.
function GrantBbModal({ creator, onClose, onSubmit }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const parsedAmount = parseWholeNumber(amount);
  const isValid = Number.isInteger(parsedAmount) && parsedAmount > 0;

  if (!creator) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isValid) return;

    setError('');
    setIsBusy(true);
    try {
      await onSubmit(creator, parsedAmount);
    } catch (err) {
      setError(err.message || 'Не удалось начислить BB');
      setIsBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Начислить BB">
      <form className="user-form" onSubmit={handleSubmit}>
        <p className="map-preview__text">
          {creator.name || 'Без имени'} · {creator.email || '—'}
          <br />
          Сейчас на балансе: {creator.bbBalance ?? 0} BB
        </p>
        <label>
          Сколько BB
          <input
            autoFocus
            inputMode="numeric"
            min={1}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="100"
            required
            step={1}
            type="number"
            value={amount}
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button className="primary-button" disabled={isBusy || !isValid} type="submit">
            {isBusy ? 'Начисляем...' : 'Начислить'}
          </button>
          <button className="ghost-button" onClick={onClose} type="button">
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}

// amount > 0 grants and approves; 0 refuses, and a refusal has to say why —
// the backend answers BB_REQUEST_REPLY_REQUIRED otherwise.
function BbRequestResolveModal({ bbRequest, onClose, onSubmit }) {
  const [amount, setAmount] = useState(() =>
    bbRequest?.requestedAmount != null ? String(bbRequest.requestedAmount) : '',
  );
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const parsedAmount = parseWholeNumber(amount);
  const isAmountValid =
    Number.isInteger(parsedAmount) && parsedAmount >= 0 && parsedAmount <= 100000;
  const isRefusal = parsedAmount === 0;
  const isValid = isAmountValid && (!isRefusal || reply.trim().length > 0);

  if (!bbRequest) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isValid) return;

    setError('');
    setIsBusy(true);
    try {
      await onSubmit(bbRequest, parsedAmount, reply.trim());
    } catch (err) {
      setError(err.message || 'Не удалось ответить на запрос');
      setIsBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ответ на запрос BB">
      <form className="user-form" onSubmit={handleSubmit}>
        <p className="map-preview__text">
          {bbRequest.creator?.name || 'Без имени'} · {bbRequest.creator?.email || '—'} · баланс{' '}
          {bbRequest.creator?.bbBalance ?? 0} BB
        </p>
        <p className="map-preview__text">«{bbRequest.message}»</p>
        <label>
          Сколько BB начислить
          <input
            autoFocus
            inputMode="numeric"
            max={100000}
            min={0}
            onChange={(event) => setAmount(event.target.value)}
            placeholder={bbRequest.requestedAmount != null ? String(bbRequest.requestedAmount) : '0'}
            required
            step={1}
            type="number"
            value={amount}
          />
        </label>
        <p className="muted">
          {bbRequest.requestedAmount != null
            ? `Деятель просит ${bbRequest.requestedAmount} BB. `
            : 'Сумму Деятель не указал. '}
          0 — отказ, и тогда ответ обязателен.
        </p>
        <label>
          Ответ Деятелю{isRefusal ? '' : ' (необязательно)'}
          <textarea
            maxLength={1000}
            onChange={(event) => setReply(event.target.value)}
            placeholder={isRefusal ? 'Почему не начисляем — это увидит Деятель' : 'Например: удачного фестиваля!'}
            required={isRefusal}
            rows={3}
            value={reply}
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button
            className={isRefusal ? 'danger-button' : 'primary-button'}
            disabled={isBusy || !isValid}
            type="submit"
          >
            {isBusy ? 'Отправляем...' : isRefusal ? 'Отказать' : 'Начислить и ответить'}
          </button>
          <button className="ghost-button" onClick={onClose} type="button">
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Days count from the later of expiresAt and now on the backend, so a faded
// artifact comes back for exactly this many days.
function ArtifactExtendModal({ artifact, onClose, onSubmit }) {
  const [days, setDays] = useState('7');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const parsedDays = parseWholeNumber(days);
  const isValid = Number.isInteger(parsedDays) && parsedDays >= 1 && parsedDays <= 365;

  if (!artifact) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isValid) return;

    setError('');
    setIsBusy(true);
    try {
      await onSubmit(artifact, parsedDays, message.trim());
    } catch (err) {
      setError(err.message || 'Не удалось добавить дни жизни');
      setIsBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Добавить дни жизни">
      <form className="user-form" onSubmit={handleSubmit}>
        <p className="map-preview__text">
          «{artifact.title || 'Без названия'}» · {getAuthorIdentity(artifact.user)} · живёт ещё{' '}
          {artifact.ttlDays ?? '—'} дн.
        </p>
        <label>
          Сколько дней добавить (1–365)
          <input
            autoFocus
            inputMode="numeric"
            max={365}
            min={1}
            onChange={(event) => setDays(event.target.value)}
            required
            step={1}
            type="number"
            value={days}
          />
        </label>
        <label>
          Сообщение автору (необязательно)
          <textarea
            maxLength={300}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={ARTIFACT_EXTEND_DEFAULT_MESSAGE}
            rows={3}
            value={message}
          />
        </label>
        <p className="muted">
          Автору придёт уведомление и пуш: «Ваш артефакт получил ещё N дней жизни» с этим
          сообщением.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button className="primary-button" disabled={isBusy || !isValid} type="submit">
            {isBusy ? 'Добавляем...' : 'Добавить дни'}
          </button>
          <button className="ghost-button" onClick={onClose} type="button">
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EmptyState({ children }) {
  return <div className="empty-state">{children}</div>;
}

const RADIUS_SOURCE_ID = 'deyatel-radius-preview';
const RADIUS_FILL_LAYER_ID = 'deyatel-radius-preview-fill';
const RADIUS_LINE_LAYER_ID = 'deyatel-radius-preview-line';

// Same great-circle approximation and visual style (color/opacity/dash) as
// the front-end demo's radius preview (lib/maps/maplibre/index.ts) — kept
// consistent across clients rather than shared as a package for two small copies.
function buildCircleRingCoordinates(center, radiusMeters, steps = 64) {
  const earthRadius = 6371000;
  const angularDistance = radiusMeters / earthRadius;
  const latRad = (center.lat * Math.PI) / 180;
  const lngRad = (center.lng * Math.PI) / 180;

  const coordinates = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(angularDistance) +
        Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearing),
    );
    const lng2 =
      lngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRad),
        Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2),
      );

    coordinates.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }

  return coordinates;
}

function buildCirclePolygonGeoJson(center, radiusMeters) {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [buildCircleRingCoordinates(center, radiusMeters)] },
      },
    ],
  };
}

const ENTITY_ZONES_SOURCE_ID = 'existing-entity-zones';
const ENTITY_ZONES_FILL_LAYER_ID = 'existing-entity-zones-fill';
const ENTITY_ZONES_LINE_LAYER_ID = 'existing-entity-zones-line';

// Same icon set the demo uses, copied into public/markers so the two clients
// read identically - an admin looking at a point should see what a visitor sees.
const MARKER_ICON_BY_ARTIFACT_TYPE = {
  text: '/markers/Text.svg',
  image: '/markers/Image.svg',
  video: '/markers/Video.svg',
  audio: '/markers/Audio.svg',
  mixed: '/markers/Puzzle.svg',
};
const CREATOR_ENTITY_ICON_BY_TYPE = {
  PLACE: '/markers/Place.svg',
  EVENT: '/markers/Event.svg',
};

// Pixel distance at which two markers merge, matching the demo's
// ClusterArtifacts so the same crowd looks the same in both clients.
const CLUSTER_RADIUS_PX = 40;
const VIEWPORT_DEBOUNCE_MS = 250;

function resolveMediaKind(mimeType) {
  if (!mimeType) return null;
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return null;
}

function getArtifactVisualType(artifact) {
  const kinds = (artifact.medias || [])
    .map((media) => resolveMediaKind(media.mimeType))
    .filter(Boolean);

  if (!kinds.length) return 'text';

  const unique = new Set(kinds);
  if (unique.size > 1) return 'mixed';
  return kinds[0];
}

// Media comes back pointing at MinIO; nginx proxies /bridges-media/ the same
// way it proxies the basemap, so the admin can show the picture itself.
function toPublicMediaUrl(url) {
  if (!url) return null;
  if (url.startsWith('/bridges-media/')) return url;

  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/bridges-media/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    return null;
  }

  return null;
}

// Erosion made visible: the closer an artifact is to expiring, the fainter its
// marker. Same thresholds as the demo.
function getArtifactOpacityByTtl(ttlDays) {
  if (ttlDays <= 0) return 0.2;
  if (ttlDays <= 1) return 0.35;
  if (ttlDays <= 3) return 0.55;
  if (ttlDays <= 7) return 0.75;
  return 1;
}

// Greedy pixel clustering, ported from the demo's ClusterArtifacts. Screen
// distance rather than metres on purpose: what needs merging is what overlaps
// visually, which depends on zoom.
function clusterByPixels(items, map) {
  if (!items.length) return [];

  const points = items.map((item) => {
    const projected = map.project([item.lng, item.lat]);
    return { item, x: projected.x, y: projected.y };
  });

  const used = new Set();
  const groups = [];

  for (const p of points) {
    if (used.has(p.item.id)) continue;

    const nearby = [p];
    used.add(p.item.id);

    for (const q of points) {
      if (used.has(q.item.id)) continue;

      const dx = p.x - q.x;
      const dy = p.y - q.y;

      if (Math.sqrt(dx * dx + dy * dy) <= CLUSTER_RADIUS_PX) {
        nearby.push(q);
        used.add(q.item.id);
      }
    }

    const members = nearby.map((n) => n.item);

    if (members.length === 1) {
      groups.push({ type: 'single', item: p.item, lat: p.item.lat, lng: p.item.lng });
    } else {
      groups.push({
        type: 'cluster',
        items: members,
        lat: members.reduce((sum, m) => sum + m.lat, 0) / members.length,
        lng: members.reduce((sum, m) => sum + m.lng, 0) / members.length,
      });
    }
  }

  return groups;
}

function buildMarkerElement({ iconSrc, alt, count, opacity }) {
  const element = document.createElement('div');
  element.className = count ? 'marker marker--cluster' : 'marker';
  if (opacity != null) element.style.opacity = String(opacity);

  const icon = document.createElement('img');
  icon.src = iconSrc;
  icon.alt = alt || '';
  element.appendChild(icon);

  if (count) {
    const badge = document.createElement('span');
    badge.className = 'marker__count';
    badge.textContent = String(count);
    element.appendChild(badge);
  }

  return element;
}

function buildEntityZonesGeoJson(entities) {
  return {
    type: 'FeatureCollection',
    features: entities.map((entity) => ({
      type: 'Feature',
      properties: { id: entity.id },
      geometry: {
        type: 'Polygon',
        coordinates: [buildCircleRingCoordinates(entity, entity.reachRadiusM || 300)],
      },
    })),
  };
}

function hasLatLng(item) {
  return Number.isFinite(item?.lat) && Number.isFinite(item?.lng);
}

// Resolved before the map is built rather than after. GeolocateControl.trigger()
// moved the camera a second or two after the map had already appeared over
// Tashkent, which read as the map jumping out from under you. The demo waits
// for the position and opens where you are; so does this now.
function resolveUserPosition() {
  if (!navigator.geolocation) return Promise.resolve(null);

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 300000 },
    );
  });
}

// Same dot the demo draws for "you are here" - MapLibre's built-in blue puck
// belongs to a different product.
function createUserDot() {
  const dot = document.createElement('div');
  dot.style.width = '14px';
  dot.style.height = '14px';
  dot.style.borderRadius = '999px';
  dot.style.border = '2px solid #fff';
  dot.style.background = '#111';
  dot.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.25)';
  return dot;
}

// Width the popover is clamped against so it never hangs off the map edge.
const MAP_POPOVER_WIDTH = 320;

function DeyatelMapPicker({
  mode,
  marker,
  radiusMeters,
  onPick,
  creatorEntities,
  accessToken,
  admin,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const overlayMarkersRef = useRef([]);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [viewportArtifacts, setViewportArtifacts] = useState([]);

  // What the admin clicked, and where it is on screen. Kept here rather than in
  // the parent because only the map can turn coordinates into pixels.
  const [selection, setSelection] = useState(null);
  const [anchor, setAnchor] = useState(null);

  // Who last decided where the camera points. A pan by the admin always wins:
  // nothing should yank the view out from under them once they have moved it.
  const cameraOwnerRef = useRef(null);
  const userMovedRef = useRef(false);

  // Read inside map event handlers, which are bound once and would otherwise
  // capture whatever these were at mount.
  const modeRef = useRef(mode);
  const onPickRef = useRef(onPick);
  modeRef.current = mode;
  onPickRef.current = onPick;

  useEffect(() => {
    let map = null;
    let cancelled = false;

    // The style is fetched before the map exists now, so creation is async and
    // the cleanup has to cope with unmounting while that is still in flight.
    (async () => {
      // In parallel: neither waits on the other, and the map is built once both
      // are known so it never has to move afterwards.
      const [style, position] = await Promise.all([getMapStyle(), resolveUserPosition()]);
      if (cancelled) return;

      map = new maplibregl.Map({
        container: containerRef.current,
        style,
        // Tashkent only when the browser would not say where we are; the data
        // fallback below still applies after that.
        center: position || TASHKENT_CENTER,
        zoom: position ? 14 : 11,
        // Second line of defence behind absolutizeStyleUrls: a source's TileJSON
        // is fetched later and its `tiles` templates arrive relative too.
        transformRequest: (url) =>
          url.startsWith(MAP_PROXY_PREFIX) ? { url: toAbsoluteMapUrl(url) } : undefined,
      });
      mapRef.current = map;

      const geolocate = new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true, timeout: 8000 },
        // The dot is ours, drawn below; this control is kept only for the
        // button that recentres on demand.
        showUserLocation: false,
        fitBoundsOptions: { maxZoom: 14 },
      });
      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.addControl(geolocate, 'top-right');

      // A real pan or zoom carries an originalEvent; a programmatic camera move
      // does not. That is the difference between "the admin chose this view" and
      // "we chose it for them".
      map.on('movestart', (event) => {
        if (event.originalEvent) userMovedRef.current = true;
      });

      geolocate.on('geolocate', () => {
        cameraOwnerRef.current = 'geolocation';
      });

      if (position) {
        cameraOwnerRef.current = 'geolocation';
        new maplibregl.Marker({ element: createUserDot(), anchor: 'center' })
          .setLngLat([position.lng, position.lat])
          .addTo(map);
      }

      map.on('load', () => setIsStyleLoaded(true));

      map.on('click', (event) => {
        if (modeRef.current !== 'place') {
          // A click on open water is how you dismiss whatever is open.
          setSelection(null);
          return;
        }
        onPickRef.current({ lat: event.lngLat.lat, lng: event.lngLat.lng });
      });
    })();

    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = mode === 'place' ? 'crosshair' : '';
  }, [mode, isStyleLoaded]);

  // The popover hangs off a coordinate, not off a corner of the screen, so it
  // has to be re-projected on every camera frame to stay under its marker.
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !selection) {
      setAnchor(null);
      return undefined;
    }

    function update() {
      const point = map.project([selection.lng, selection.lat]);
      const width = containerRef.current?.clientWidth ?? 0;
      const half = MAP_POPOVER_WIDTH / 2 + 8;

      setAnchor({
        x: Math.min(Math.max(point.x, half), Math.max(half, width - half)),
        y: point.y,
      });
    }

    update();
    map.on('move', update);
    map.on('zoom', update);

    return () => {
      map.off('move', update);
      map.off('zoom', update);
    };
  }, [selection, isStyleLoaded]);

  // Artifacts by viewport, like the demo: the admin database holds every
  // artifact ever made, and none of them matter except the ones on screen.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return undefined;

    let timeout = null;
    let cancelled = false;

    async function loadViewport() {
      const bounds = map.getBounds();
      const center = map.getCenter();
      const radiusMeters = Math.ceil(Math.max(300, center.distanceTo(bounds.getNorthEast())));

      try {
        const found = await request(
          `/artifacts/radius?lat=${center.lat}&lng=${center.lng}&radius=${radiusMeters}`,
          {},
          accessToken,
        );
        if (!cancelled) setViewportArtifacts(Array.isArray(found) ? found : []);
      } catch {
        // A failed viewport fetch leaves the previous markers in place rather
        // than blanking the map mid-pan.
      }
    }

    function schedule() {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(loadViewport, VIEWPORT_DEBOUNCE_MS);
    }

    loadViewport();
    map.on('moveend', schedule);

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      map.off('moveend', schedule);
    };
  }, [accessToken, isStyleLoaded]);

  // Fallback for denied or unavailable geolocation: frame whatever already
  // exists, which beats a city the admin has nothing to do with.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (userMovedRef.current || cameraOwnerRef.current) return;

    const points = (creatorEntities || []).filter(hasLatLng);
    if (!points.length) return;

    const bounds = points.reduce(
      (acc, point) => acc.extend([point.lng, point.lat]),
      new maplibregl.LngLatBounds(
        [points[0].lng, points[0].lat],
        [points[0].lng, points[0].lat],
      ),
    );

    cameraOwnerRef.current = 'data';
    map.fitBounds(bounds, { padding: 64, maxZoom: 14, duration: 0 });
  }, [creatorEntities, isStyleLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!marker) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    if (!markerRef.current) {
      markerRef.current = new maplibregl.Marker({ color: '#2563eb' });
    }

    markerRef.current.setLngLat([marker.lng, marker.lat]).addTo(map);
  }, [marker, isStyleLoaded]);

  // Reach zones stay GeoJSON polygons - they are areas, not pins, and the demo
  // draws them the same way.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;

    const zones = buildEntityZonesGeoJson((creatorEntities || []).filter(hasLatLng));
    const source = map.getSource(ENTITY_ZONES_SOURCE_ID);

    if (source) {
      source.setData(zones);
      return;
    }

    const beforeId = map.getLayer(RADIUS_FILL_LAYER_ID) ? RADIUS_FILL_LAYER_ID : undefined;
    map.addSource(ENTITY_ZONES_SOURCE_ID, { type: 'geojson', data: zones });
    map.addLayer(
      {
        id: ENTITY_ZONES_FILL_LAYER_ID,
        type: 'fill',
        source: ENTITY_ZONES_SOURCE_ID,
        paint: { 'fill-color': '#E0A458', 'fill-opacity': 0.1 },
      },
      beforeId,
    );
    map.addLayer(
      {
        id: ENTITY_ZONES_LINE_LAYER_ID,
        type: 'line',
        source: ENTITY_ZONES_SOURCE_ID,
        paint: { 'line-color': '#E0A458', 'line-width': 1, 'line-opacity': 0.6 },
      },
      beforeId,
    );
  }, [creatorEntities, isStyleLoaded]);

  // Clustering is by screen distance, so the markers have to be rebuilt after
  // every camera move, not only when the data changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return undefined;

    function addGroup(group, { iconSrc, alt, opacity, kind }) {
      const isCluster = group.type === 'cluster';
      const element = buildMarkerElement({
        iconSrc,
        alt,
        count: isCluster ? group.items.length : 0,
        opacity,
      });

      element.addEventListener('click', (event) => {
        event.stopPropagation();
        // Opening a cluster used to mean zooming until it fell apart, which at
        // a dense spot meant zooming to the limit. It now lists what is inside.
        setSelection(
          isCluster
            ? { kind: 'cluster', items: group.items, itemKind: kind, lat: group.lat, lng: group.lng }
            : { kind, data: group.item, lat: group.item.lat, lng: group.item.lng },
        );
      });

      overlayMarkersRef.current.push(
        new maplibregl.Marker({ element, anchor: 'bottom' })
          .setLngLat([group.lng, group.lat])
          .addTo(map),
      );
    }

    function render() {
      overlayMarkersRef.current.forEach((m) => m.remove());
      overlayMarkersRef.current = [];

      for (const group of clusterByPixels(viewportArtifacts.filter(hasLatLng), map)) {
        const first = group.type === 'cluster' ? group.items[0] : group.item;
        addGroup(group, {
          kind: 'artifact',
          iconSrc:
            group.type === 'cluster'
              ? MARKER_ICON_BY_ARTIFACT_TYPE.mixed
              : MARKER_ICON_BY_ARTIFACT_TYPE[getArtifactVisualType(first)],
          alt: group.type === 'cluster' ? 'Кластер артефактов' : first.title,
          opacity: group.type === 'cluster' ? 1 : getArtifactOpacityByTtl(first.ttlDays ?? 0),
        });
      }

      for (const group of clusterByPixels((creatorEntities || []).filter(hasLatLng), map)) {
        const items = group.type === 'cluster' ? group.items : [group.item];
        const allEvents = items.every((item) => item.type === 'EVENT');
        addGroup(group, {
          kind: 'entity',
          iconSrc: allEvents
            ? CREATOR_ENTITY_ICON_BY_TYPE.EVENT
            : CREATOR_ENTITY_ICON_BY_TYPE.PLACE,
          alt: group.type === 'cluster' ? 'Кластер точек' : items[0].title,
        });
      }
    }

    render();
    map.on('moveend', render);
    map.on('zoomend', render);

    return () => {
      map.off('moveend', render);
      map.off('zoomend', render);
      overlayMarkersRef.current.forEach((m) => m.remove());
      overlayMarkersRef.current = [];
    };
  }, [viewportArtifacts, creatorEntities, isStyleLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;

    if (!marker) {
      if (map.getLayer(RADIUS_FILL_LAYER_ID)) map.removeLayer(RADIUS_FILL_LAYER_ID);
      if (map.getLayer(RADIUS_LINE_LAYER_ID)) map.removeLayer(RADIUS_LINE_LAYER_ID);
      if (map.getSource(RADIUS_SOURCE_ID)) map.removeSource(RADIUS_SOURCE_ID);
      return;
    }

    const geojson = buildCirclePolygonGeoJson(marker, radiusMeters);
    const source = map.getSource(RADIUS_SOURCE_ID);

    if (source) {
      source.setData(geojson);
      return;
    }

    map.addSource(RADIUS_SOURCE_ID, { type: 'geojson', data: geojson });
    map.addLayer({
      id: RADIUS_FILL_LAYER_ID,
      type: 'fill',
      source: RADIUS_SOURCE_ID,
      paint: { 'fill-color': '#E0A458', 'fill-opacity': 0.18 },
    });
    map.addLayer({
      id: RADIUS_LINE_LAYER_ID,
      type: 'line',
      source: RADIUS_SOURCE_ID,
      paint: { 'line-color': '#E0A458', 'line-width': 1.5, 'line-dasharray': [2, 2] },
    });
  }, [marker, radiusMeters, isStyleLoaded]);

  return (
    <div className="map-picker-wrap">
      <div className="map-picker" ref={containerRef} />

      {selection && anchor && (
        <div
          className="map-popover"
          style={{ left: `${anchor.x}px`, top: `${anchor.y + 14}px`, width: MAP_POPOVER_WIDTH }}
        >
          {selection.kind === 'cluster' ? (
            <MapClusterList
              itemKind={selection.itemKind}
              items={selection.items}
              onClose={() => setSelection(null)}
              onPick={(item) =>
                setSelection({
                  kind: selection.itemKind,
                  data: item,
                  lat: item.lat,
                  lng: item.lng,
                })
              }
            />
          ) : (
            <MapObjectPreview
              admin={admin}
              artifact={selection.kind === 'artifact' ? selection.data : null}
              entity={selection.kind === 'entity' ? selection.data : null}
              onClose={() => setSelection(null)}
            />
          )}
        </div>
      )}

      <div className="map-legend">
        <span>
          <img alt="" src={CREATOR_ENTITY_ICON_BY_TYPE.PLACE} /> Места
        </span>
        <span>
          <img alt="" src={CREATOR_ENTITY_ICON_BY_TYPE.EVENT} /> События
        </span>
        <span>
          <img alt="" src={MARKER_ICON_BY_ARTIFACT_TYPE.mixed} /> Артефакты
        </span>
      </div>
    </div>
  );
}

function MapClusterList({ items, itemKind, onPick, onClose }) {
  return (
    <div className="map-popover__body">
      <div className="map-preview__head">
        <h3>
          {itemKind === 'artifact' ? 'Артефакты' : 'Точки Деятелей'}: {items.length}
        </h3>
        <button className="ghost-button" onClick={onClose} type="button">
          Закрыть
        </button>
      </div>

      <ul className="map-cluster-list">
        {items.map((item) => (
          <li key={item.id}>
            <button onClick={() => onPick(item)} type="button">
              <span className="map-cluster-list__title">{item.title || 'Без названия'}</span>
              <span className="map-cluster-list__meta">
                {itemKind === 'artifact'
                  ? `${item.ttlDays} дн.`
                  : CREATOR_ENTITY_TYPE_LABEL[item.type] || item.type}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function normalizeTag(tag) {
  return String(tag || '').trim().replace(/^#/, '').toLowerCase();
}

// One renderer for an artifact wherever it is looked at: the card under a map
// marker, the modal opened from a table, the preview of what a tag deletion is
// about to touch.
function ArtifactDetails({ artifact }) {
  const image = (artifact.medias || [])
    .map((media) => (resolveMediaKind(media.mimeType) === 'image' ? toPublicMediaUrl(media.url) : null))
    .find(Boolean);

  return (
    <>
      {image && <img alt={artifact.title || ''} className="map-preview__image" src={image} />}
      {artifact.description && <p className="map-preview__text">{artifact.description}</p>}

      <dl className="map-preview__facts">
        <div>
          <dt>Автор</dt>
          <dd title={getAuthorIdentity(artifact.user)}>{getAuthorIdentity(artifact.user)}</dd>
        </div>
        <div>
          <dt>Тип</dt>
          <dd>{artifact.type || 'TEXT'}</dd>
        </div>
        <div>
          <dt>Живёт ещё</dt>
          <dd>{artifact.ttlDays ?? '—'} дн.</dd>
        </div>
        <div>
          <dt>Реакции</dt>
          <dd>
            +{artifact.likeCount || 0} / -{artifact.dislikeCount || 0} / ★
            {artifact.favoriteCount || 0}
          </dd>
        </div>
        <div>
          <dt>Создан</dt>
          <dd>{formatDate(artifact.createdAt)}</dd>
        </div>
        <div>
          <dt>Состояние</dt>
          <dd>{artifact.isHidden ? 'Скрыт' : 'Виден'}</dd>
        </div>
      </dl>
    </>
  );
}

function EntityDetails({ entity }) {
  return (
    <dl className="map-preview__facts">
      <div>
        <dt>Деятель</dt>
        <dd title={getIdentity(entity.creator)}>{getIdentity(entity.creator)}</dd>
      </div>
      <div>
        <dt>Тип</dt>
        <dd>{CREATOR_ENTITY_TYPE_LABEL[entity.type] || entity.type}</dd>
      </div>
      <div>
        <dt>Радиус</dt>
        <dd>{entity.reachRadiusM} м</dd>
      </div>
      <div>
        <dt>Реакции</dt>
        <dd>
          +{entity.likeCount || 0} / -{entity.dislikeCount || 0} / ★{entity.favoriteCount || 0}
        </dd>
      </div>
      <div>
        <dt>Создана</dt>
        <dd>{formatDate(entity.createdAt)}</dd>
      </div>
    </dl>
  );
}

// Tags are per-artifact in the UI but global in the API — deleting one strips
// it from every artifact that carries it. The × says so, and the confirmation
// it opens shows exactly which ones.
function ArtifactTagList({ artifact, onDeleteTag }) {
  if (!Array.isArray(artifact.tags) || !artifact.tags.length) return null;

  return (
    <div className="tag-chips">
      {artifact.tags.map((tag) => (
        <span className="tag-chip" key={tag}>
          #{tag}
          {onDeleteTag && (
            <button
              onClick={() => onDeleteTag(tag)}
              title={`Удалить тег #${tag} во всех артефактах`}
              type="button"
            >
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function ObjectAdminActions({ artifact, entity, admin }) {
  if (!admin) return null;

  if (artifact) {
    return (
      <div className="object-actions">
        <button
          className="ghost-button"
          onClick={() => admin.onToggleArtifactHidden(artifact.id, !artifact.isHidden)}
          type="button"
        >
          {artifact.isHidden ? 'Вернуть' : 'Скрыть'}
        </button>
        <button
          className="ghost-button"
          onClick={() => admin.onExtendArtifact(artifact)}
          type="button"
        >
          Добавить дни жизни
        </button>
        <button
          className="danger-button"
          onClick={() => admin.onDeleteArtifact(artifact)}
          type="button"
        >
          Удалить
        </button>
      </div>
    );
  }

  return (
    <div className="object-actions">
      <button className="danger-button" onClick={() => admin.onDeleteEntity(entity)} type="button">
        Удалить точку
      </button>
    </div>
  );
}

function MapObjectPreview({ artifact, entity, onClose, admin }) {
  const item = artifact || entity;
  if (!item) return null;

  return (
    <aside className="map-preview">
      <div className="map-preview__head">
        <h3>{item.title || 'Без названия'}</h3>
        <button className="ghost-button" onClick={onClose} type="button">
          Закрыть
        </button>
      </div>

      {artifact ? <ArtifactDetails artifact={artifact} /> : <EntityDetails entity={entity} />}

      {artifact && <ArtifactTagList artifact={artifact} onDeleteTag={admin?.onDeleteTag} />}

      <ObjectAdminActions admin={admin} artifact={artifact} entity={entity} />
    </aside>
  );
}

// Opened from the tables, which until now could only list artifacts and never
// show one.
function ArtifactViewModal({ artifact, admin, onClose }) {
  if (!artifact) return null;

  return (
    <Modal onClose={onClose} title={artifact.title || 'Артефакт'}>
      <div className="artifact-modal">
        <ArtifactDetails artifact={artifact} />
        <ArtifactTagList artifact={artifact} onDeleteTag={admin?.onDeleteTag} />
        <ObjectAdminActions admin={admin} artifact={artifact} />
      </div>
    </Modal>
  );
}

// Deleting a tag is a bulk edit of other people's artifacts, so it says which
// ones before it happens, and each is openable.
function TagImpactModal({ tag, artifacts, onCancel, onConfirm, onInspect }) {
  if (!tag) return null;

  return (
    <Modal onClose={onCancel} title={`Удалить тег #${tag}`}>
      <div className="artifact-modal">
        <p className="map-preview__text">
          {artifacts.length
            ? `Тег будет снят с ${artifacts.length} артефакт(ов). Сами артефакты останутся.`
            : 'Ни один загруженный артефакт этот тег не использует.'}
        </p>

        {artifacts.length > 0 && (
          <ul className="map-cluster-list">
            {artifacts.map((artifact) => (
              <li key={artifact.id}>
                <button onClick={() => onInspect(artifact)} type="button">
                  <span className="map-cluster-list__title">
                    {artifact.title || 'Без названия'}
                  </span>
                  <span className="map-cluster-list__meta">
                    {getAuthorIdentity(artifact.user)} · {artifact.ttlDays ?? '—'} дн.
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="object-actions">
          <button className="ghost-button" onClick={onCancel} type="button">
            Отмена
          </button>
          <button className="danger-button" onClick={onConfirm} type="button">
            Удалить тег
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MapPanel({ accessToken, creatorEntities, onCreated, admin }) {
  // Viewing is the default. Placing a point is the rarer, deliberate act, and
  // making it the default meant every click meant to inspect something moved
  // the pin instead.
  const [mode, setMode] = useState('view');
  const [form, setForm] = useState(emptyDeyatelForm);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState('');

  const hasCoords = form.lat != null && form.lng != null;

  async function handleSubmit(event) {
    event.preventDefault();
    setNotice('');
    setError('');
    setResult(null);

    if (!hasCoords) {
      setError('Отметьте точку на карте');
      return;
    }

    setIsSubmitting(true);

    const body = {
      email: form.email.trim() || undefined,
      name: form.name.trim() || undefined,
      place: {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        lat: form.lat,
        lng: form.lng,
        reachRadiusM: Number(form.reachRadiusM) || undefined,
      },
    };

    try {
      const payload = await request(
        '/admin-onboarding/b2c',
        { method: 'POST', body: JSON.stringify(body) },
        accessToken,
      );

      setResult(payload.data);
      setLastSubmittedEmail(body.email ?? '');
      setNotice(
        body.email
          // Brdgs mail still lands in Gmail spam often enough that the admin
          // needs to be able to tell the Деятель where to look (ClickUp 86bbu00j4).
          ? `Точка привязана к ${body.email} — письмо с паролем отправлено. Если оно не придёт за пару минут, попросите проверить папку «Спам»`
          : 'Ссылка создана — отправьте её Деятелю',
      );
      setForm(emptyDeyatelForm);
      // The new point should show up on the map straight away, and the map no
      // longer navigates anywhere after a submit.
      onCreated?.();
    } catch (err) {
      setError(err.message || 'Не удалось создать точку');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="map-workspace">
      <DeyatelMapPicker
        mode={mode}
        marker={hasCoords ? { lat: form.lat, lng: form.lng } : null}
        radiusMeters={Number(form.reachRadiusM) || 300}
        onPick={({ lat, lng }) => setForm((current) => ({ ...current, lat, lng }))}
        creatorEntities={creatorEntities}
        accessToken={accessToken}
        admin={admin}
      />

      <div className="map-mode-switch">
        <button
          className={mode === 'place' ? 'primary-button' : 'ghost-button'}
          onClick={() => setMode((current) => (current === 'place' ? 'view' : 'place'))}
          type="button"
        >
          {mode === 'place' ? 'Готово, вернуться к просмотру' : 'Разместить точку Деятелю'}
        </button>
        {mode === 'place' && <span className="map-mode-hint">Кликните на карте</span>}
      </div>

      {mode === 'place' && (
        <section className="deyatel-floating-form">
          <div className="section-heading">
            <h2>Точка для Деятеля</h2>
          </div>

          <form className="user-form" onSubmit={handleSubmit}>
            <input
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="Email Деятеля, если уже известен (необязательно)"
              type="email"
            />
            <input
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Имя Деятеля/бизнеса (необязательно)"
            />
            <input
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              placeholder="Название точки"
              required
            />
            <input
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
              placeholder="Описание (необязательно)"
            />
            <div>
              <label className="text-[11px] text-gray-500">
                Радиус охвата: {form.reachRadiusM} м
              </label>
              <input
                value={form.reachRadiusM}
                onChange={(event) =>
                  setForm((current) => ({ ...current, reachRadiusM: Number(event.target.value) }))
                }
                min={10}
                max={2000}
                step={10}
                type="range"
                style={{ width: '100%' }}
              />
            </div>

            <p className="eyebrow">
              {hasCoords
                ? `Точка: ${form.lat.toFixed(5)}, ${form.lng.toFixed(5)}`
                : 'Кликните на карте, чтобы поставить точку'}
            </p>

            {error && <p className="form-error">{error}</p>}

            <div className="form-actions">
              <button className="primary-button" disabled={isSubmitting} type="submit">
                {isSubmitting
                  ? 'Создаём...'
                  : form.email.trim()
                    ? 'Создать и привязать к email'
                    : 'Создать и получить ссылку'}
              </button>
            </div>
          </form>

          {notice && <div className="message">{notice}</div>}

          {result?.inviteUrl && (
            <div className="message">
              Ссылка для Деятеля{lastSubmittedEmail ? ' (на случай, если письмо не дойдёт)' : ''}:
              <br />
              <code>{result.inviteUrl}</code>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function AdminApp({ auth, onLogout }) {
  // The queue is what an admin comes for first.
  const [activeTab, setActiveTab] = useState('moderation');
  const [isSidebarCompact, setIsSidebarCompact] = useState(false);
  const [viewedArtifact, setViewedArtifact] = useState(null);
  const [pendingTag, setPendingTag] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportedArtifacts, setReportedArtifacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [creatorEntities, setCreatorEntities] = useState([]);
  const [moderationRequests, setModerationRequests] = useState([]);
  const [potentialUsers, setPotentialUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [creators, setCreators] = useState([]);
  const [bbRequests, setBbRequests] = useState([]);
  const [userReports, setUserReports] = useState([]);
  const [routeRequests, setRouteRequests] = useState([]);
  const [routeHistory, setRouteHistory] = useState([]);
  // Pending names and avatars come with the dashboard; decided ones are
  // history, fetched only when the admin opens «Решённые».
  const [profileRequests, setProfileRequests] = useState([]);
  const [profileRequestHistory, setProfileRequestHistory] = useState([]);
  const [moderationMode, setModerationMode] = useState('PENDING');
  const [moderationType, setModerationType] = useState('');
  const [grantingCreator, setGrantingCreator] = useState(null);
  const [resolvingBbRequest, setResolvingBbRequest] = useState(null);
  const [extendingArtifact, setExtendingArtifact] = useState(null);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Success notices used to stay on screen forever: nothing cleared them, and
  // this panel stays mounted while tabs switch, so a "тег удалён" banner
  // followed the admin into every other section (ClickUp 86bbu0fp3).
  // Errors are left alone deliberately — those should persist until acted on.
  useEffect(() => {
    if (!notice) return undefined;

    const timeout = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    setNotice('');
    setError('');
  }, [activeTab]);

  // The map wants the room. Collapsing on arrival rather than locking the
  // sidebar shut, so the admin can still open it back up while on the map.
  useEffect(() => {
    if (activeTab === 'map') setIsSidebarCompact(true);
  }, [activeTab]);

  const moderationQueue = useMemo(
    () =>
      buildModerationQueue({
        reportedArtifacts,
        moderationRequests,
        profileRequests,
        bbRequests,
        userReports,
        routeRequests,
      }),
    [reportedArtifacts, moderationRequests, profileRequests, bbRequests, userReports, routeRequests],
  );
  const moderationHistory = useMemo(
    () =>
      buildModerationHistory({
        reports,
        profileRequestHistory,
        bbRequests,
        userReports,
        routeHistory,
      }),
    [reports, profileRequestHistory, bbRequests, userReports, routeHistory],
  );

  const metrics = useMemo(
    () => [
      { label: 'На модерации', value: moderationQueue.length },
      { label: 'Артефактов', value: artifacts.length },
      { label: 'Пользователей', value: users.length },
      { label: 'Точек Деятелей', value: creatorEntities.length },
      { label: 'Потенциальных пользователей', value: potentialUsers.length },
      { label: 'Тегов', value: tags.length },
    ],
    [
      moderationQueue.length,
      artifacts.length,
      users.length,
      creatorEntities.length,
      potentialUsers.length,
      tags.length,
    ],
  );

  async function loadDashboard() {
    setIsLoading(true);
    setError('');

    try {
      const [
        nextArtifacts,
        nextReports,
        nextReportedArtifacts,
        nextUsers,
        nextCreatorEntities,
        nextModerationRequests,
        nextPotentialUsers,
        nextTags,
        nextCreators,
        nextBbRequests,
        nextProfileRequests,
        nextUserReports,
        nextRouteRequests,
      ] = await Promise.all([
        request('/artifacts?filter=all', {}, auth.accessToken),
        request('/reports', {}, auth.accessToken),
        request('/reports/artifacts', {}, auth.accessToken),
        request('/users', {}, auth.accessToken),
        request('/creator-entities/admin/all', {}, auth.accessToken),
        request('/creator-entities/admin/requests', {}, auth.accessToken),
        request('/potential-users', {}, auth.accessToken),
        request('/artifacts/tags', {}, auth.accessToken),
        request('/creator-wallet/admin/creators', {}, auth.accessToken),
        // All of them: the queue takes the pending ones, «Решённые» the rest.
        request('/bb-requests/admin', {}, auth.accessToken),
        request('/profiles/admin/requests?status=PENDING', {}, auth.accessToken),
        request('/signals/admin/reports', {}, auth.accessToken),
        request('/routes/admin/requests', {}, auth.accessToken),
      ]);

      setArtifacts(nextArtifacts);
      setReports(nextReports);
      setReportedArtifacts(nextReportedArtifacts);
      setUsers(nextUsers);
      setCreatorEntities(nextCreatorEntities);
      setModerationRequests(nextModerationRequests);
      setPotentialUsers(nextPotentialUsers);
      setTags(nextTags);
      setCreators(nextCreators);
      setBbRequests(nextBbRequests);
      setProfileRequests(nextProfileRequests);
      setUserReports(nextUserReports);
      setRouteRequests(nextRouteRequests);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить данные');
    } finally {
      setIsLoading(false);
    }
  }

  async function changeModerationMode(mode) {
    setModerationMode(mode);
    setModerationType('');
    setError('');

    // The pending queue is already here from the dashboard.
    if (mode === 'PENDING') return;

    setIsLoading(true);

    try {
      const [approved, rejected, decidedRoutes] = await Promise.all([
        request('/profiles/admin/requests?status=APPROVED', {}, auth.accessToken),
        request('/profiles/admin/requests?status=REJECTED', {}, auth.accessToken),
        // The latest 100 route decisions, newest first.
        request('/routes/admin/decided', {}, auth.accessToken),
      ]);
      setProfileRequestHistory([...approved, ...rejected]);
      setRouteHistory(decidedRoutes);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить решённые обращения');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUserSubmit(event) {
    event.preventDefault();
    setNotice('');
    setError('');

    const body = {
      name: userForm.name.trim(),
      email: userForm.email.trim() || undefined,
      phone: userForm.phone.trim() || undefined,
      role: userForm.role,
      password: userForm.password || undefined,
    };

    try {
      if (userForm.id) {
        await request(
          `/users/${userForm.id}`,
          { method: 'PATCH', body: JSON.stringify(body) },
          auth.accessToken,
        );
        setNotice('Пользователь обновлён');
      } else {
        await request('/users', { method: 'POST', body: JSON.stringify(body) }, auth.accessToken);
        setNotice('Пользователь добавлен');
      }

      setUserForm(emptyUserForm);
      setIsUserFormOpen(false);
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось сохранить пользователя');
    }
  }

  function editUser(user) {
    setUserForm({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'USER',
      password: '',
    });
    setIsUserFormOpen(true);
  }

  function openCreateUserForm() {
    setUserForm(emptyUserForm);
    setIsUserFormOpen(true);
  }

  function closeUserForm() {
    setUserForm(emptyUserForm);
    setIsUserFormOpen(false);
  }

  async function deleteArtifact(artifact) {
    if (!window.confirm(`Удалить артефакт «${artifact.title || artifact.id}»? Это необратимо.`)) {
      return;
    }

    setNotice('');
    setError('');

    try {
      await request(`/artifacts/${artifact.id}`, { method: 'DELETE' }, auth.accessToken);
      setViewedArtifact(null);
      setNotice('Артефакт удалён');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось удалить артефакт');
    }
  }

  // Tag deletion is global, so it goes through a confirmation that lists what
  // it touches rather than a window.confirm that names only the tag.
  const taggedArtifacts = pendingTag
    ? artifacts.filter((artifact) =>
        (artifact.tags || []).some((tag) => normalizeTag(tag) === normalizeTag(pendingTag)),
      )
    : [];

  async function deleteTag(tag) {
    setNotice('');
    setError('');

    try {
      await request(`/artifacts/tags/${encodeURIComponent(tag)}`, { method: 'DELETE' }, auth.accessToken);
      setPendingTag(null);
      setNotice(`Тег «${tag}» удалён`);
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось удалить тег');
    }
  }

  async function deleteUser(userId) {
    setNotice('');
    setError('');

    try {
      await request(`/users/${userId}`, { method: 'DELETE' }, auth.accessToken);
      setNotice('Пользователь удалён');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось удалить пользователя');
    }
  }

  async function updateReportStatus(reportId, status) {
    setNotice('');
    setError('');

    try {
      await request(
        `/reports/${reportId}`,
        { method: 'PATCH', body: JSON.stringify({ status }) },
        auth.accessToken,
      );
      setNotice(status === 'RESOLVED' ? 'Жалоба отмечена решённой' : 'Жалоба отклонена');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось обновить жалобу');
    }
  }

  async function decideUserReport(reportId, status) {
    setNotice('');
    setError('');

    try {
      const payload = await request(
        `/signals/admin/reports/${reportId}`,
        { method: 'PATCH', body: JSON.stringify({ status }) },
        auth.accessToken,
      );
      setUserReports((current) =>
        current.map((item) => (item.id === reportId ? { ...item, ...payload } : item)),
      );
      setNotice(status === 'RESOLVED' ? 'Жалоба отмечена решённой' : 'Жалоба отклонена');
    } catch (err) {
      setError(err.message || 'Не удалось обновить жалобу');
    }
  }

  async function approveModerationRequest(entityId) {
    setNotice('');
    setError('');

    try {
      await request(
        `/creator-entities/admin/${entityId}/approve`,
        { method: 'POST' },
        auth.accessToken,
      );
      setNotice('Точка опубликована, Деятель получит уведомление');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось одобрить заявку');
    }
  }

  async function rejectModerationRequest(entityId, reason) {
    setNotice('');
    setError('');

    try {
      await request(
        `/creator-entities/admin/${entityId}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
        auth.accessToken,
      );
      setNotice('Заявка отклонена, Деятель получит уведомление с причиной');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось отклонить заявку');
    }
  }

  // A decided route just leaves the queue: there is no admin list of decided
  // routes to move it to, and nothing else on the dashboard counts them.
  async function approveRoute(route) {
    setNotice('');
    setError('');

    try {
      await request(`/routes/admin/${route.id}/approve`, { method: 'POST' }, auth.accessToken);
      setRouteRequests((current) => current.filter((item) => item.id !== route.id));
      setNotice(`Маршрут «${route.title}» опубликован на 14 дней, автор получит уведомление`);
    } catch (err) {
      setError(err.message || 'Не удалось одобрить маршрут');
    }
  }

  async function rejectRoute(route, reason) {
    setNotice('');
    setError('');

    try {
      await request(
        `/routes/admin/${route.id}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
        auth.accessToken,
      );
      setRouteRequests((current) => current.filter((item) => item.id !== route.id));
      setNotice(`Маршрут «${route.title}» отклонён, автор увидит причину`);
    } catch (err) {
      setError(err.message || 'Не удалось отклонить маршрут');
    }
  }

  async function deleteCreatorEntity(entityId, notifyByEmail) {
    setNotice('');
    setError('');

    try {
      await request(
        `/creator-entities/admin/${entityId}`,
        { method: 'DELETE', body: JSON.stringify({ notifyByEmail }) },
        auth.accessToken,
      );
      setNotice(notifyByEmail ? 'Точка удалена, Деятель уведомлён по email' : 'Точка удалена');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось удалить точку');
    }
  }

  async function setArtifactHidden(artifactId, hidden) {
    setNotice('');
    setError('');

    try {
      await request(
        `/artifacts/${artifactId}/${hidden ? 'hide' : 'unhide'}`,
        { method: 'POST' },
        auth.accessToken,
      );
      setNotice(hidden ? 'Артефакт скрыт' : 'Артефакт снова виден');
      await loadDashboard();
    } catch (err) {
      setError(err.message || 'Не удалось изменить видимость артефакта');
    }
  }

  // Changed in place from the PATCH answer rather than through loadDashboard:
  // one row, eleven requests otherwise. The edit form never sends tier, so
  // saving it cannot undo this.
  async function setUserTier(user, tier) {
    setNotice('');
    setError('');

    try {
      const payload = await request(
        `/users/${user.id}`,
        { method: 'PATCH', body: JSON.stringify({ tier }) },
        auth.accessToken,
      );
      const updated = payload?.data || { ...user, tier };
      setUsers((current) =>
        current.map((item) => (item.id === user.id ? { ...item, ...updated } : item)),
      );
      setNotice(
        tier === 'INFLUENCER'
          ? `${user.name || 'Пользователь'} теперь Творец`
          : `${user.name || 'Пользователь'} больше не Творец`,
      );
    } catch (err) {
      setError(err.message || 'Не удалось изменить статус Творца');
    }
  }

  // A Деятель's balance shows both in «Деятели» and next to each of their BB
  // requests; both follow the wallet's answer.
  function applyCreatorBalance(creatorId, bbBalance, changes = {}) {
    setCreators((current) =>
      current.map((creator) =>
        creator.id === creatorId ? { ...creator, bbBalance, ...changes } : creator,
      ),
    );
    setBbRequests((current) =>
      current.map((bbRequest) =>
        bbRequest.creator?.id === creatorId
          ? { ...bbRequest, creator: { ...bbRequest.creator, bbBalance } }
          : bbRequest,
      ),
    );
  }

  // Errors are left to the modal that called this — it shows them inline.
  async function grantBb(creator, amount) {
    const payload = await request(
      `/creator-wallet/${creator.id}/grant`,
      { method: 'POST', body: JSON.stringify({ amount }) },
      auth.accessToken,
    );
    const bbBalance = payload?.data?.bbBalance ?? (creator.bbBalance || 0) + amount;

    applyCreatorBalance(creator.id, bbBalance);
    setGrantingCreator(null);
    setNotice(`Начислено ${amount} BB, баланс: ${bbBalance} BB`);
  }

  async function resolveBbRequest(bbRequest, amount, reply) {
    const payload = await request(
      `/bb-requests/admin/${bbRequest.id}/resolve`,
      { method: 'POST', body: JSON.stringify({ amount, reply: reply || undefined }) },
      auth.accessToken,
    );
    // The answer is the bare request, without the creator block the list has.
    const resolved = { ...bbRequest, ...payload?.data, creator: bbRequest.creator };
    const creatorId = bbRequest.creator?.id || bbRequest.creatorId;
    const bbBalance = (bbRequest.creator?.bbBalance || 0) + amount;

    setBbRequests((current) =>
      current.map((item) => (item.id === bbRequest.id ? resolved : item)),
    );
    applyCreatorBalance(creatorId, bbBalance, { hasPendingBbRequest: false });
    setResolvingBbRequest(null);
    setNotice(
      amount > 0
        ? `Начислено ${amount} BB, Деятель увидит ответ`
        : 'Запрос отклонён, Деятель увидит ответ',
    );
  }

  async function approveProfileRequest(profileRequest) {
    setNotice('');
    setError('');

    try {
      await request(
        `/profiles/admin/requests/${profileRequest.id}/approve`,
        { method: 'POST' },
        auth.accessToken,
      );
      setProfileRequests((current) => current.filter((item) => item.id !== profileRequest.id));
      setUsers((current) =>
        current.map((user) => {
          if (user.id !== profileRequest.userId) return user;
          return profileRequest.kind === 'NAME'
            ? { ...user, name: profileRequest.name }
            : { ...user, avatarUrl: profileRequest.avatarUrl };
        }),
      );
      setNotice(
        profileRequest.kind === 'NAME'
          ? `Имя «${profileRequest.name}» одобрено`
          : 'Аватар одобрен',
      );
    } catch (err) {
      setError(err.message || 'Не удалось одобрить заявку');
    }
  }

  async function rejectProfileRequest(profileRequest, reason) {
    setNotice('');
    setError('');

    try {
      await request(
        `/profiles/admin/requests/${profileRequest.id}/reject`,
        { method: 'POST', body: JSON.stringify({ reason: reason || undefined }) },
        auth.accessToken,
      );
      setProfileRequests((current) => current.filter((item) => item.id !== profileRequest.id));
      setNotice('Заявка отклонена, пользователь получит уведомление');
    } catch (err) {
      setError(err.message || 'Не удалось отклонить заявку');
    }
  }

  // Only the lifetime changes, so only it is copied over: the admin listings
  // carry fields (reports, _count) the mutation answer does not.
  async function extendArtifact(artifact, days, message) {
    const payload = await request(
      `/artifacts/${artifact.id}/admin-extend`,
      { method: 'POST', body: JSON.stringify({ days, message: message || undefined }) },
      auth.accessToken,
    );
    const lifetime = {
      expiresAt: payload?.data?.expiresAt,
      ttlDays: payload?.data?.ttlDays,
    };
    const apply = (item) => (item.id === artifact.id ? { ...item, ...lifetime } : item);

    setArtifacts((current) => current.map(apply));
    setReportedArtifacts((current) => current.map(apply));
    setViewedArtifact((current) => (current ? apply(current) : current));
    setExtendingArtifact(null);
    setNotice(`Артефакту добавлено ${days} дн. — автору ушло уведомление и пуш`);
  }

  // Handed to the map cards and to the modal, so an artifact offers the same
  // actions wherever it is opened from.
  const adminActions = {
    onExtendArtifact: setExtendingArtifact,
    onToggleArtifactHidden: setArtifactHidden,
    onDeleteArtifact: deleteArtifact,
    onDeleteEntity: (entity) => {
      if (!window.confirm(`Удалить точку «${entity.title}»? Это необратимо.`)) return;
      deleteCreatorEntity(entity.id, true);
    },
    onDeleteTag: setPendingTag,
  };

  return (
    <main className={isSidebarCompact ? 'admin-shell admin-shell--compact' : 'admin-shell'}>
      <aside className="sidebar">
        <div className="brand">
          <img alt="Brdgs" className="brand-mark" src="/logo/LogoMark.svg" />
          <div className="brand-text">
            <p className="eyebrow">Админка</p>
            <h1 className="brand-wordmark">brdgs</h1>
          </div>
        </div>

        {/* On the sidebar's own edge rather than next to the logo, where an
            arrow beside the bridge mark read as part of the logo. */}
        <button
          className="sidebar-toggle"
          onClick={() => setIsSidebarCompact((current) => !current)}
          title={isSidebarCompact ? 'Развернуть меню' : 'Свернуть меню'}
          type="button"
        >
          <NavIcon name={isSidebarCompact ? 'chevronRight' : 'chevronLeft'} />
        </button>

        <nav>
          {tabs.map((tab) => (
            <button
              className={activeTab === tab.id ? 'nav-button active' : 'nav-button'}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
              type="button"
            >
              <NavIcon name={tab.id} />
              <span className="nav-label">{tab.label}</span>
              {tab.id === 'moderation' && moderationQueue.length > 0 && (
                <span className="nav-badge">{moderationQueue.length}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <span>{auth.user.name}</span>
          <small>{auth.user.role}</small>
          <button
            className="ghost-button logout-button"
            onClick={onLogout}
            title="Выйти"
            type="button"
          >
            <NavIcon name="logout" />
            <span className="nav-label">Выйти</span>
          </button>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div className="metrics">
            {metrics.map((metric) => (
              <Metric key={metric.label} {...metric} />
            ))}
          </div>
        </header>

        {(notice || error) && (
          <div className={error ? 'message error' : 'message'}>{error || notice}</div>
        )}

        {activeTab === 'map' ? (
          // Deliberately outside the content panel: the card's padding, border
          // and heading only ate into the map, and the section name is already
          // highlighted in the sidebar.
          <MapPanel
            accessToken={auth.accessToken}
            admin={adminActions}
            creatorEntities={creatorEntities}
            onCreated={loadDashboard}
          />
        ) : (
        <section className="content-panel">
          <div className="section-heading">
            <h2>{tabs.find((tab) => tab.id === activeTab)?.label}</h2>
            {isLoading && <span>Загрузка...</span>}
          </div>

          <div className="tab-content">
            {activeTab === 'artifacts' &&
              (artifacts.length ? (
                <ArtifactTable
                  artifacts={artifacts}
                  onExtend={setExtendingArtifact}
                  onToggleHidden={setArtifactHidden}
                  onView={setViewedArtifact}
                />
              ) : (
                <EmptyState>Артефактов нет</EmptyState>
              ))}

            {activeTab === 'moderation' && (
              <ModerationPanel
                handlers={{
                  onApprovePoint: approveModerationRequest,
                  onApproveRoute: approveRoute,
                  onRejectRoute: rejectRoute,
                  onDecideUserReport: decideUserReport,
                  onApproveProfile: approveProfileRequest,
                  onExtendArtifact: setExtendingArtifact,
                  onRejectPoint: rejectModerationRequest,
                  onRejectProfile: rejectProfileRequest,
                  onResolveBbRequest: setResolvingBbRequest,
                  onToggleArtifactHidden: setArtifactHidden,
                  onUpdateReportStatus: updateReportStatus,
                  onViewArtifact: setViewedArtifact,
                }}
                history={moderationHistory}
                mode={moderationMode}
                onModeChange={changeModerationMode}
                onTypeChange={setModerationType}
                queue={moderationQueue}
                type={moderationType}
              />
            )}

            {activeTab === 'users' && (
              <UsersPanel
                form={userForm}
                isFormOpen={isUserFormOpen}
                onCancel={closeUserForm}
                onDelete={deleteUser}
                onEdit={editUser}
                onOpenCreate={openCreateUserForm}
                onSetTier={setUserTier}
                onSubmit={handleUserSubmit}
                setForm={setUserForm}
                users={users}
              />
            )}

            {activeTab === 'creatorEntities' &&
              (creatorEntities.length ? (
                <CreatorEntityTable entities={creatorEntities} onDelete={deleteCreatorEntity} />
              ) : (
                <EmptyState>Точек Деятелей пока нет</EmptyState>
              ))}

            {activeTab === 'creators' &&
              (creators.length ? (
                <CreatorsTable creators={creators} onGrant={setGrantingCreator} />
              ) : (
                <EmptyState>Деятелей пока нет</EmptyState>
              ))}

            {activeTab === 'potentialUsers' &&
              (potentialUsers.length ? (
                <PotentialUsersTable potentialUsers={potentialUsers} />
              ) : (
                <EmptyState>Пока никто не оставил почту в конце онбординга</EmptyState>
              ))}

            {activeTab === 'tags' &&
              (tags.length ? (
                <TagsTable onDelete={setPendingTag} tags={tags} />
              ) : (
                <EmptyState>Тегов пока нет</EmptyState>
              ))}
          </div>
        </section>
        )}
      </section>

      <ArtifactViewModal
        admin={adminActions}
        artifact={viewedArtifact}
        onClose={() => setViewedArtifact(null)}
      />

      <TagImpactModal
        artifacts={taggedArtifacts}
        onCancel={() => setPendingTag(null)}
        onConfirm={() => deleteTag(pendingTag)}
        onInspect={(artifact) => {
          setPendingTag(null);
          setViewedArtifact(artifact);
        }}
        tag={pendingTag}
      />

      {/* After ArtifactViewModal on purpose: opened from it, this one has to
          land on top. Keyed so each target starts from a clean form. */}
      {extendingArtifact && (
        <ArtifactExtendModal
          artifact={extendingArtifact}
          key={extendingArtifact.id}
          onClose={() => setExtendingArtifact(null)}
          onSubmit={extendArtifact}
        />
      )}

      {grantingCreator && (
        <GrantBbModal
          creator={grantingCreator}
          key={grantingCreator.id}
          onClose={() => setGrantingCreator(null)}
          onSubmit={grantBb}
        />
      )}

      {resolvingBbRequest && (
        <BbRequestResolveModal
          bbRequest={resolvingBbRequest}
          key={resolvingBbRequest.id}
          onClose={() => setResolvingBbRequest(null)}
          onSubmit={resolveBbRequest}
        />
      )}
    </main>
  );
}

function App() {
  const [auth, setAuth] = useState(() => {
    try {
      const saved = window.localStorage.getItem(AUTH_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  function handleLogin(nextAuth) {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
    setAuth(nextAuth);
  }

  function handleLogout() {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuth(null);
  }

  useEffect(() => {
    function syncAuth() {
      try {
        const saved = window.localStorage.getItem(AUTH_STORAGE_KEY);
        setAuth(saved ? JSON.parse(saved) : null);
      } catch {
        setAuth(null);
      }
    }

    window.addEventListener(AUTH_CHANGED_EVENT, syncAuth);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, syncAuth);
  }, []);

  if (!auth?.accessToken || auth?.user?.role !== 'ADMIN') {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return <AdminApp auth={auth} onLogout={handleLogout} />;
}

export default App;
